import { useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Avatar,
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  TextField,
  Stack,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  Tooltip,
  Button,
  DialogTitle,
  DialogActions,
  Tabs,
  Tab,
  Checkbox,
  InputAdornment,
  useMediaQuery,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import UploadIcon from "@mui/icons-material/UploadOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import EditIcon from "@mui/icons-material/EditOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import SensorsOutlinedIcon from "@mui/icons-material/SensorsOutlined";
import GroupWorkOutlinedIcon from "@mui/icons-material/GroupWorkOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import PlaylistAddCheckCircleOutlinedIcon from "@mui/icons-material/PlaylistAddCheckCircleOutlined";
import CircleIcon from "@mui/icons-material/Circle";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import RestoreFromTrashOutlinedIcon from "@mui/icons-material/RestoreFromTrashOutlined";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import AutorenewOutlinedIcon from "@mui/icons-material/AutorenewOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import DeviceHubRoundedIcon from "@mui/icons-material/DeviceHubRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import InsertChartOutlinedRoundedIcon from "@mui/icons-material/InsertChartOutlinedRounded";
import SettingsSuggestRoundedIcon from "@mui/icons-material/SettingsSuggestRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import AssignmentTurnedInRoundedIcon from "@mui/icons-material/AssignmentTurnedInRounded";
import BluetoothSearchingRoundedIcon from "@mui/icons-material/BluetoothSearchingRounded";
import GroupAddRoundedIcon from "@mui/icons-material/GroupAddRounded";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import FileDownloadDoneRoundedIcon from "@mui/icons-material/FileDownloadDoneRounded";
import DataObjectRoundedIcon from "@mui/icons-material/DataObjectRounded";
import StraightenRoundedIcon from "@mui/icons-material/StraightenRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import ApartmentRoundedIcon from "@mui/icons-material/ApartmentRounded";
import SyncRoundedIcon from "@mui/icons-material/SyncRounded";
import DeleteSweepRoundedIcon from "@mui/icons-material/DeleteSweepRounded";
import OpenInFullRoundedIcon from "@mui/icons-material/OpenInFullRounded";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";

import { useSelector } from "react-redux";
import { skipToken } from "@reduxjs/toolkit/query/react";

import RegisterDeviceModal from "../admin/RegisterDeviceModal";
import InstallationCertificateModal from "../admin/InstallationCertificateModal";
import ReportsPortal from "../reports/ReportsPortal";
import SensorAssignModal from "./SensorAssignModal";
import { useStatus } from "../../popup/NotificationContext";
import {
  useBulkDeleteDevicesMutation,
  useDeleteDeviceMutation,
  useGetAllUsersQuery,
  useGetAssignableUsersQuery,
  useGetDeviceExpiringPageV2Query,
  useGetDeviceListPageV2Query,
  useGetDeviceRenewalsPageV2Query,
  useEditDeviceMutation,
  useGetDeviceNotificationTemplateQuery,
  usePermanentlyDeleteDeviceMutation,
  useRevealUserPasswordMutation,
  useRestoreDeviceMutation,
} from "../../redux/admin/adminApi";
import { useAddDevicesToGroupMutation, useGetAllGroupsQuery } from "../../redux/admin/groupApi";
import { useGetFleetSummaryQuery, useLazyGetDeviceV2ByIdQuery } from "../../redux/admin/deviceApi";
import { downloadEntityTemplate, uploadEntityFile } from "../../services/importExportService";
import {
  useCreateNotificationSettingMutation,
  useUpdateNotificationSettingMutation,
} from "../../redux/admin/notificationApi";
import { getDeviceActionsEffective, normalizeRole, ROLES } from "../../utils/rbacHelper";
import {
  CUSTOM_ROWS_PER_PAGE_OPTIONS,
  getDisplayRange,
  getRowsPerPageLabel,
  getTotalPagesForRows,
  ROWS_PER_PAGE_MENU_PROPS,
} from "../../utils/tablePagination";
import { getDeviceStatus as resolveVehicleStatus } from "../../utils/vehicleStatus";
import { mergeLegacyDeviceDetail } from "../../utils/deviceV2Adapter";
import { readDeviceViewCache, writeDeviceViewCache } from "../../utils/deviceViewCache";

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 1.5,
    backgroundColor: "#fff",
  },
};

const COMPACT_DIALOG_PAPER_SX = {
  borderRadius: 2,
  overflow: "hidden",
  width: "min(92vw, 520px)",
  maxWidth: "520px",
};

const NARROW_DIALOG_PAPER_SX = {
  borderRadius: 2,
  overflow: "hidden",
  width: "min(92vw, 460px)",
  maxWidth: "460px",
};

const FILTER_OPTIONS = [
  { value: "imei", label: "IMEI" },
  { value: "vehicle", label: "Vehicle no" },
  { value: "sim", label: "Sim" },
  { value: "status", label: "Status" },
  { value: "model", label: "Model" },
  { value: "vehicleType", label: "Vehicle type" },
  { value: "group", label: "Group" },
  { value: "customer", label: "Customer" },
  { value: "dealer", label: "Dealer" },
  { value: "deleted", label: "Deleted Device" },
  { value: "contact", label: "Contact" },
  { value: "creationDate", label: "Creation Date" },
];

const DEVICE_LIST_MODAL_PAGE_CACHE_SCOPE = "device-list-modal-page-v2";

const STATUS_FILTER_OPTIONS = [
  { value: "running", label: "Running" },
  { value: "idle", label: "Idle" },
  { value: "stopped", label: "Stopped" },
  { value: "not reporting", label: "Not Reporting", searchText: "inactive offline no reporting no active" },
  { value: "no data", label: "No Data" },
  { value: "expired", label: "Expired" },
];

const EXTEND_VALIDITY_OPTIONS = [1, 2, 3, 4, 5, 6];

const PRIMARY_TABS = [
  { value: "devices", label: "Devices List" },
  { value: "renewal", label: "Renewal Report" },
  { value: "expiring", label: "Expiring Devices" },
  { value: "deleted", label: "Deleted Devices" },
];

const START_OF_DAY = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());
const END_OF_DAY = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
const EMPTY_COUNTS = Object.freeze({});

const normalizeDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const toTimestampMs = (value) => {
  if (value === null || value === undefined || value === "") return 0;
  if (typeof value === "number" && Number.isFinite(value)) return value < 1e12 ? value * 1000 : value;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return 0;
    if (/^\d+$/.test(trimmed)) {
      const numeric = Number(trimmed);
      if (Number.isFinite(numeric)) return numeric < 1e12 ? numeric * 1000 : numeric;
    }
  }
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatDate = (value, withTime = false) => {
  const date = normalizeDate(value);
  if (!date) return "-";
  return date.toLocaleString("en-IN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    ...(withTime
      ? {
          hour: "numeric",
          minute: "2-digit",
          second: "2-digit",
          hour12: true,
        }
      : {}),
  });
};

const numberValue = (value, fallback = 0) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

const addYears = (value, years) => {
  const base = normalizeDate(value) || new Date();
  const next = new Date(base);
  next.setMonth(next.getMonth() + Number(years || 0) * 12);
  return next;
};

const getLicenseInfo = (device) => {
  const license = device?.deviceLicense || {};
  const runtime = device?.licenseRuntime || {};
  const expiryDate = runtime?.expiryDate || license?.expiryDate || null;
  const expiresAt = normalizeDate(expiryDate);
  const now = new Date();
  const diffDays = expiresAt ? Math.ceil((START_OF_DAY(expiresAt) - START_OF_DAY(now)) / 86400000) : null;
  const type = license?.licenseType || device?.licenseType || "Yearly";
  return {
    expiryDate,
    expiresAt,
    diffDays,
    type: String(type).replace(/_/g, " "),
  };
};

const getDeviceStatus = (device) => {
  const rawStatus = String(resolveVehicleStatus(device) || "no_data");
  
  // Normalize status for consistent filtering
  const normalized = rawStatus.toLowerCase().replace(/_/g, " ").trim();
  
  // Map specific statuses to standard values
  if (normalized.includes("expired")) return "expired";
  if (normalized.includes("not reporting") || normalized.includes("offline")) return "not reporting";
  if (normalized.includes("inactive")) return "inactive";
  if (normalized.includes("unknown")) return "no data";
  if (normalized.includes("no data") || normalized === "no_data" || normalized === "nodata") return "no data";
  if (normalized.includes("running")) return "running";
  if (normalized.includes("stop")) return "stopped";
  if (normalized.includes("idle") || normalized.includes("idling")) return "idle";
  
  return normalized;
};

const getDisplayDeviceStatus = (device, licenseInfo = getLicenseInfo(device)) => {
  const rawStatus = normalizeStatusBucket(getDeviceStatus(device));

  if (licenseInfo?.diffDays != null && licenseInfo.diffDays < 0) return "Expired";
  if (rawStatus === "expired") return "Expired";

  if (rawStatus === "inactive") {
    return "No Reporting";
  }

  if (["unknown", "no data", "no_data", "nodata"].includes(rawStatus)) {
    return "No Data";
  }

  if (["running", "moving"].includes(rawStatus)) {
    return "Running";
  }

  if (["idle", "idling", "ignition on", "acc on", "engine on", "engine start", "started"].includes(rawStatus)) {
    return "Idle";
  }

  if (["stopped", "parked", "ignition off", "acc off", "engine off", "engine stop"].includes(rawStatus)) {
    return "Stopped";
  }

  return "No Data";
};

const getStatusColor = (status) => {
  const value = String(status || "").toLowerCase();
  if (value.includes("running")) return "#10b981";
  if (value.includes("stop")) return "#dc2626";
  if (value.includes("idle")) return "#f59e0b";
  if (value.includes("inactive") || value.includes("no reporting")) return "#0ea5e9";
  if (value.includes("no data")) return "#6b7280";
  if (value.includes("expired")) return "#ef4444";
  return "#f43f5e";
};

const extractRemarks = (device) => {
  const customField = (Array.isArray(device?.customFields) ? device.customFields : []).find(
    (field) => String(field?.key || "").toLowerCase() === "remarks" || String(field?.fieldName || "").toLowerCase() === "remarks"
  );
  return customField?.fieldValue || device?.remarks || "-";
};

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value == null) return [];
  return [value];
};

const displayName = (value) => {
  if (!value) return "-";
  if (typeof value === "string") return value;
  return value?.username || value?.name || value?.email || value?.uniqueId || value?.deviceId || "-";
};

const resolveCreatedByLabel = (value) => {
  if (!value) return "-";
  if (typeof value === "string") return String(value).trim() || "-";
  const username = String(value?.username || value?.name || "").trim();
  const uniqueId = String(value?.uniqueId || value?.userId || value?.deviceId || "").trim();
  if (username && uniqueId) return `${username} (${uniqueId})`;
  return username || uniqueId || String(value?.email || "").trim() || "-";
};

const getEntityId = (value) => String(value?._id || value?.id || value || "").trim();

const getUniqueEntityEntries = (value) => {
  const uniqueEntries = new Map();

  toArray(value).forEach((entry) => {
    if (!entry) return;
    const id = getEntityId(entry);
    const label = String(displayName(entry) || "").trim();
    const key = id || label.toLowerCase();
    if (!key || uniqueEntries.has(key)) return;
    uniqueEntries.set(key, { id, label: label || id || "-" });
  });

  return Array.from(uniqueEntries.values());
};

const getUniqueEntityEntriesWithDirectory = (value, directoryMap) => {
  const uniqueEntries = new Map();

  toArray(value).forEach((entry) => {
    if (!entry) return;
    const id = getEntityId(entry);
    const directoryEntry = id ? directoryMap?.get(id) : null;
    const candidate = entry && typeof entry === "object" ? entry : directoryEntry || entry;
    const label = String(displayName(candidate) || "").trim();
    const key = id || label.toLowerCase();
    if (!key || uniqueEntries.has(key)) return;
    uniqueEntries.set(key, { id, label: label || id || "-" });
  });

  return Array.from(uniqueEntries.values());
};

const getEntitySummary = (entity) => {
  if (!entity) {
    return { id: "", name: "-", uniqueId: "-", mobileNo: "-" };
  }

  return {
    id: String(entity?._id || entity?.id || "").trim(),
    name: entity?.username || entity?.name || entity?.email || entity?.uniqueId || "-",
    uniqueId: entity?.uniqueId || entity?.userId || entity?.loginId || entity?.email || "-",
    mobileNo: entity?.mobileNo || entity?.contactNumber || entity?.phone || entity?.mobile || "-",
  };
};

const FULL_DEVICE_ACCESS_ROLES = new Set([
  ROLES.SUPER_ADMIN,
  ROLES.MANAGER,
  ROLES.HR,
  ROLES.OFFICE_EMPLOYEE,
]);

const getRelatedUserIds = (value) => {
  const ids = new Set();
  toArray(value).forEach((entry) => {
    const id = getEntityId(entry);
    if (id) ids.add(id);
  });
  return Array.from(ids);
};

const canAccessDeviceForRole = ({ device, currentUser, currentUserRole }) => {
  if (!device) return false;
  if (
    [
      ROLES.SUB_ADMIN,
      ROLES.DEALER,
      ROLES.DRIVER,
      ROLES.CLIENT,
      ROLES.USER,
      ROLES.VIEWER,
    ].includes(currentUserRole)
  ) {
    // Device-list API is already backend-scoped for these roles.
    // Avoid hiding valid rows again on the client when ownership comes via dealer/sub-admin hierarchy.
    return true;
  }
  if (FULL_DEVICE_ACCESS_ROLES.has(currentUserRole)) return true;

  const currentUserId = String(currentUser?._id || currentUser?.id || "").trim();
  if (!currentUserId) return false;

  const relationIds = new Set([
    ...getRelatedUserIds(device?.dealer),
    ...getRelatedUserIds(device?.assignedToUser),
    ...getRelatedUserIds(device?.assignedToUsers),
    ...getRelatedUserIds(device?.assignedToViewers),
    ...getRelatedUserIds(device?.adminId),
    ...getRelatedUserIds(device?.driver?.driverId),
    ...getRelatedUserIds(device?.createdBy),
    ...getRelatedUserIds(device?.ownerUser),
    ...getRelatedUserIds(device?.user),
    ...getRelatedUserIds(device?.customer),
  ]);

  if (relationIds.has(currentUserId)) return true;
  return false;
};

const normalizeFilterValue = (value) => String(value || "").trim().toLowerCase();

const normalizeStatusBucket = (value) => {
  const normalized = normalizeFilterValue(value).replace(/_/g, " ");
  if (["inactive", "not reporting", "notreporting", "offline"].includes(normalized)) return "inactive";
  if (["unknown", "no data", "nodata"].includes(normalized)) return "no data";
  if (["stopped", "stop", "parked"].includes(normalized)) return "stopped";
  if (["idle", "idling"].includes(normalized)) return "idle";
  if (["running", "moving"].includes(normalized)) return "running";
  if (normalized.includes("expired")) return "expired";
  return normalized;
};

const isSameDay = (left, right) =>
  left instanceof Date &&
  right instanceof Date &&
  !Number.isNaN(left.getTime()) &&
  !Number.isNaN(right.getTime()) &&
  left.getFullYear() === right.getFullYear() &&
  left.getMonth() === right.getMonth() &&
  left.getDate() === right.getDate();

const matchesSummaryBucket = (row, summaryTab) => {
  if (!row || summaryTab === "all") return true;
  const now = new Date();
  const createdAt = row.creationTs instanceof Date ? row.creationTs : null;
  const expiryAt = row.licenseExpiryTs instanceof Date ? row.licenseExpiryTs : null;

  if (summaryTab === "noData") {
    return normalizeStatusBucket(row.statusKey) === "no data";
  }

  if (summaryTab === "today") {
    return isSameDay(createdAt, now);
  }

  if (summaryTab === "month") {
    return (
      createdAt &&
      createdAt.getFullYear() === now.getFullYear() &&
      createdAt.getMonth() === now.getMonth()
    );
  }

  if (summaryTab === "expiringToday") {
    return isSameDay(expiryAt, now);
  }

  if (summaryTab === "expiringWeek") {
    if (!expiryAt) return false;
    const diffDays = Math.ceil((START_OF_DAY(expiryAt) - START_OF_DAY(now)) / 86400000);
    return diffDays >= 0 && diffDays <= 7;
  }

  if (summaryTab === "expiringMonth") {
    if (!expiryAt) return false;
    const diffDays = Math.ceil((START_OF_DAY(expiryAt) - START_OF_DAY(now)) / 86400000);
    return diffDays >= 0 && diffDays <= 30;
  }

  return true;
};

const matchesLocalDeviceFilter = ({ row, filterBy, query, primaryTab, summaryTab }) => {
  if (!row) return false;
  const normalizedQuery = normalizeFilterValue(query);

  if (primaryTab === "deleted") {
    if (!row.deleted) return false;
  } else if (primaryTab === "devices") {
    if (row.deleted) return false;
    if (!matchesSummaryBucket(row, summaryTab)) return false;
  }

  if (!normalizedQuery) return true;

  if (filterBy === "dealer") {
    return row.dealerIds.some((id) => String(id) === String(query)) ||
      normalizeFilterValue(row.dealers).includes(normalizedQuery);
  }

  if (filterBy === "customer") {
    return row.userIds.some((id) => String(id) === String(query)) ||
      normalizeFilterValue(row.users).includes(normalizedQuery);
  }

  if (filterBy === "group") {
    return String(row.groupId || "") === String(query) ||
      normalizeFilterValue(row.groupName).includes(normalizedQuery);
  }

  if (filterBy === "status") {
    return normalizeStatusBucket(row.statusKey) === normalizeStatusBucket(query) ||
      normalizeFilterValue(row.status).includes(normalizedQuery);
  }

  const haystacksByFilter = {
    imei: [row.imei],
    vehicle: [row.vehicle],
    sim: [row.simNo],
    model: [row.model],
    vehicleType: [row.raw?.vehicleType, row.icon],
    deleted: [row.disabled, row.deleted ? "deleted yes true" : "active no false"],
    contact: [row.contact],
    creationDate: [row.creationTime],
  };

  const haystack = (haystacksByFilter[filterBy] || [
    row.vehicle,
    row.imei,
    row.simNo,
    row.model,
    row.contact,
  ])
    .filter(Boolean)
    .join(" ");

  return normalizeFilterValue(haystack).includes(normalizedQuery);
};

const DEFAULT_NOTIFICATION_CHANNELS = ["socket", "push", "web"];

function DeviceNotificationSettingsDialog({ device, open, onClose, showStatus }) {
  const [search, setSearch] = useState("");
  const [busyKey, setBusyKey] = useState("");
  const deviceId = device?._id || device?.id || null;
  const {
    data: templatePayload,
    isFetching,
    refetch: refetchTemplate,
  } = useGetDeviceNotificationTemplateQuery(deviceId, { skip: !open || !deviceId });
  const [createNotificationSetting] = useCreateNotificationSettingMutation();
  const [updateNotificationSetting] = useUpdateNotificationSettingMutation();

  const canEdit = Boolean(templatePayload?.canEdit);
  const ownerName = templatePayload?.ownerUser?.username || "";
  const templateRows = useMemo(
    () => (Array.isArray(templatePayload?.alerts) ? templatePayload.alerts : []),
    [templatePayload]
  );

  const visibleRows = useMemo(() => {
    const query = normalizeFilterValue(search);
    if (!query) return templateRows;
    return templateRows.filter((item) => normalizeFilterValue(item.label).includes(query));
  }, [search, templateRows]);

  const handleToggle = async (item, checked) => {
    if (!deviceId || !canEdit) return;

    const nextChannels = checked
      ? toArray(item.channels).filter(Boolean).length
        ? Array.from(new Set(toArray(item.channels).filter(Boolean)))
        : DEFAULT_NOTIFICATION_CHANNELS
      : [];
    const payload = {
      alarmType: item.alarmType,
      allDevices: false,
      deviceId,
      channels: nextChannels,
      isActive: nextChannels.length > 0,
      sound: item?.sound || "default",
      priority: item?.priority || "medium",
    };

    setBusyKey(item.key);
    try {
      if (item?.settingId && item?.scope === "device") {
        await updateNotificationSetting({ id: item.settingId, ...payload }).unwrap();
      } else {
        await createNotificationSetting(payload).unwrap();
      }
      await refetchTemplate();
      showStatus("success", `${item.label} notification ${checked ? "enabled" : "disabled"}.`);
    } catch (error) {
      showStatus("error", error?.data?.msg || "Failed to update notification setting.");
    } finally {
      setBusyKey("");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={busyKey ? undefined : onClose}
      fullWidth={false}
      maxWidth={false}
      PaperProps={{ sx: { ...COMPACT_DIALOG_PAPER_SX, borderRadius: 2.5 } }}
    >
      <Box sx={{ px: 3, py: 2.5, bgcolor: "#fff", borderBottom: "1px solid #e5e7eb", display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <Box>
          <Typography sx={{ fontSize: { xs: 28, sm: 38 }, lineHeight: 1, fontWeight: 900, color: "#111" }}>
            Notifications
          </Typography>
          <Typography sx={{ mt: 0.75, fontSize: 14, color: "#6b7280", fontWeight: 600 }}>
            {device?.name || device?.vehicleNumber || device?.imei || "Selected device"}
          </Typography>
          {!canEdit && ownerName ? (
            <Typography sx={{ mt: 0.75, fontSize: 13, color: "#6b7280", fontWeight: 600 }}>
              Controlled by {ownerName}. View only.
            </Typography>
          ) : null}
        </Box>
        <IconButton onClick={onClose} disabled={Boolean(busyKey)} sx={{ color: "#111", mt: -0.5, mr: -1 }}>
          <CloseIcon sx={{ fontSize: 34 }} />
        </IconButton>
      </Box>

      <DialogContent sx={{ bgcolor: "#f7f7f6", px: 3, py: 2.5 }}>
        <TextField
          fullWidth
          placeholder="Search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          sx={{
            mb: 2,
            "& .MuiOutlinedInput-root": {
              bgcolor: "#fff",
              borderRadius: 1.5,
              fontSize: 22,
              fontWeight: 700,
              pr: 1,
            },
            "& .MuiOutlinedInput-input": {
              py: 1.8,
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon sx={{ color: "#ff5f7e", fontSize: 42 }} />
              </InputAdornment>
            ),
          }}
        />

        {isFetching ? (
          <Box sx={{ py: 5, display: "flex", justifyContent: "center" }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Stack spacing={0.75}>
            {visibleRows.map((item) => {
              const checked = Boolean(item?.checked);
              const saving = busyKey === item.key;

              return (
                <Box
                  key={item.key}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    minHeight: 46,
                  }}
                >
                  <Checkbox
                    checked={checked}
                    disabled={saving || !canEdit}
                    onChange={(event) => handleToggle(item, event.target.checked)}
                    sx={{
                      p: 0.5,
                      color: "#1976d2",
                      "&.Mui-checked": { color: "#1976d2" },
                    }}
                  />
                  <Typography sx={{ fontSize: 23, lineHeight: 1.2, color: "#1f2937", fontWeight: 500 }}>
                    {item.label}
                  </Typography>
                  {saving ? <CircularProgress size={16} sx={{ ml: 0.75 }} /> : null}
                </Box>
              );
            })}

            {!visibleRows.length ? (
              <Typography sx={{ py: 3, textAlign: "center", color: "#6b7280", fontWeight: 600 }}>
                No notifications found.
              </Typography>
            ) : null}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}

const buildCsv = (rows) => {
  const headers = [
    "Vehicle",
    "IMEI",
    "Group",
    "Status",
    "Last Update",
    "Dealer",
    "User",
    "Sim No",
    "Model",
    "Contact",
    "Icon",
    "Disabled",
    "License Expiration Time",
    "Creation Time",
    "Todays Odo",
    "Total Odo",
    "License Type",
    "Remarks",
    "Integration",
  ];

  const body = rows.map((row) =>
    [
      row.vehicle,
      row.imei,
      row.groupName,
      row.status,
      row.lastUpdate,
      row.dealers,
      row.users,
      row.simNo,
      row.model,
      row.contact,
      row.icon,
      row.disabled,
      row.licenseExpiryTime,
      row.creationTime,
      row.todaysOdo,
      row.totalOdo,
      row.licenseType,
      row.remarks,
      row.integration,
    ]
      .map((value) => `"${String(value ?? "").replace(/"/g, '""')}"`)
      .join(",")
  );

  return [headers.join(","), ...body].join("\n");
};

const stickySx = (left, width, zIndex = 3) => ({
  position: "sticky",
  left,
  minWidth: width,
  width,
  zIndex,
  backgroundColor: "#fff",
  backgroundClip: "padding-box",
  overflow: "hidden",
  boxShadow: "1px 0 0 #e5e7eb",
});

const SELECTED_TABLE_ROW_BG = "#dfeafe";

const PAGE_BG = "linear-gradient(180deg, #eef4ff 0%, #f8fbff 32%, #ffffff 100%)";
const SHOW_LEGACY_TOP_SUMMARY_CARDS = false;
const STATUS_CHART_COLORS = {
  Running: "#22c55e",
  Idle: "#f59e0b",
  Stopped: "#ef4444",
  "No Reporting": "#0ea5e9",
  Expired: "#a855f7",
  "No Data": "#94a3b8",
};

const formatCompactCount = (value) =>
  new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(Number(value || 0));

const formatGrowth = (current, previous) => {
  const safeCurrent = Number(current || 0);
  const safePrevious = Number(previous || 0);
  if (safePrevious <= 0) return safeCurrent > 0 ? "+100%" : "+0%";
  const delta = ((safeCurrent - safePrevious) / safePrevious) * 100;
  const rounded = Math.round(delta);
  return `${rounded >= 0 ? "+" : ""}${rounded}%`;
};

const shortDateLabel = (value) => {
  const date = normalizeDate(value);
  if (!date) return "—";
  return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
};

const monthLabel = (value) => {
  const date = normalizeDate(value);
  if (!date) return "—";
  return date.toLocaleDateString("en-IN", { month: "short" });
};

const licenseTone = (diffDays) => {
  if (diffDays == null) return { label: "No expiry", color: "#64748b", bg: "#f1f5f9" };
  if (diffDays < 0) return { label: "Expired", color: "#b91c1c", bg: "#fee2e2" };
  if (diffDays <= 7) return { label: `${diffDays} day${diffDays === 1 ? "" : "s"} left`, color: "#b45309", bg: "#fef3c7" };
  if (diffDays <= 30) return { label: `${diffDays} days left`, color: "#0369a1", bg: "#e0f2fe" };
  return { label: `${diffDays} days left`, color: "#166534", bg: "#dcfce7" };
};

function DeviceMetricCard({ title, value, helper, icon, accent = "#2563eb", active = false, onClick }) {
  return (
    <Button
      onClick={onClick}
      variant="text"
      sx={{
        p: 0,
        minWidth: 0,
        textTransform: "none",
        width: "100%",
        borderRadius: 3,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          p: 2.1,
          textAlign: "left",
          borderRadius: 3,
          border: `1px solid ${active ? alpha(accent, 0.28) : "#e2e8f0"}`,
          bgcolor: active ? alpha(accent, 0.08) : "#fff",
          boxShadow: active ? `0 18px 40px ${alpha(accent, 0.14)}` : "0 14px 34px rgba(15, 23, 42, 0.06)",
          transition: "all 180ms ease",
          "&:hover": {
            transform: "translateY(-1px)",
            boxShadow: `0 20px 40px ${alpha(accent, 0.16)}`,
          },
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1.25}>
          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#475569" }}>{title}</Typography>
            <Typography sx={{ mt: 1, fontSize: 28, lineHeight: 1, fontWeight: 900, color: "#0f172a" }}>
              {formatCompactCount(value)}
            </Typography>
            <Typography sx={{ mt: 0.8, fontSize: 12.5, color: "#64748b", fontWeight: 600 }}>{helper}</Typography>
          </Box>
          <Avatar
            sx={{
              width: 46,
              height: 46,
              bgcolor: alpha(accent, 0.12),
              color: accent,
              border: `1px solid ${alpha(accent, 0.14)}`,
            }}
          >
            {icon}
          </Avatar>
        </Stack>
      </Paper>
    </Button>
  );
}

function DeviceAnalyticsCard({ title, subtitle, children, action }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, md: 2.5 },
        borderRadius: 3,
        border: "1px solid #e2e8f0",
        boxShadow: "0 20px 50px rgba(15, 23, 42, 0.06)",
        bgcolor: "#fff",
        minHeight: 320,
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1.5}>
        <Box>
          <Typography sx={{ fontSize: 17, fontWeight: 800, color: "#0f172a" }}>{title}</Typography>
          <Typography sx={{ mt: 0.5, fontSize: 13, color: "#64748b", fontWeight: 600 }}>{subtitle}</Typography>
        </Box>
        {action || null}
      </Stack>
      <Box sx={{ mt: 2.25, height: 230 }}>{children}</Box>
    </Paper>
  );
}

function DeviceActionTile({ icon, title, helper, tone = "blue", onClick, danger = false }) {
  const palette = {
    blue: { color: "#2563eb", bg: "#eff6ff", border: "#bfdbfe" },
    green: { color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0" },
    purple: { color: "#7c3aed", bg: "#f5f3ff", border: "#ddd6fe" },
    amber: { color: "#ca8a04", bg: "#fffbeb", border: "#fde68a" },
    red: { color: "#dc2626", bg: "#fef2f2", border: "#fecaca" },
    slate: { color: "#475569", bg: "#f8fafc", border: "#e2e8f0" },
  };
  const toneMeta = danger ? palette.red : palette[tone] || palette.blue;
  const compactLabel = String(title || "").trim().split(/\s+/).slice(0, 2).join(" ");

  return (
    <Button
      onClick={onClick}
      variant="text"
      sx={{
        p: 0,
        minWidth: 0,
        width: "100%",
        borderRadius: 2.5,
        textTransform: "none",
      }}
    >
      <Box
        sx={{
          width: "100%",
          minHeight: 82,
          px: 0.9,
          py: 1.1,
          textAlign: "center",
          borderRadius: 2,
          border: `1px solid ${toneMeta.border}`,
          bgcolor: toneMeta.bg,
          transition: "all 180ms ease",
          "&:hover": {
            transform: "translateY(-1px)",
            boxShadow: `0 14px 26px ${alpha(toneMeta.color, 0.14)}`,
          },
        }}
      >
        <Stack spacing={0.7} alignItems="center" justifyContent="center">
          <Avatar sx={{ width: 34, height: 34, bgcolor: alpha(toneMeta.color, 0.12), color: toneMeta.color }}>
            {icon}
          </Avatar>
          <Typography
            sx={{
              fontSize: 11,
              lineHeight: 1.15,
              fontWeight: 800,
              color: "#0f172a",
              maxWidth: "100%",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {compactLabel}
          </Typography>
        </Stack>
      </Box>
    </Button>
  );
}

function DeviceInfoRow({ label, value, icon }) {
  return (
    <Stack direction="row" spacing={1.1} alignItems="flex-start">
      <Avatar sx={{ width: 28, height: 28, bgcolor: "#f8fafc", color: "#2563eb", border: "1px solid #dbeafe" }}>
        {icon}
      </Avatar>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: 0.28, color: "#94a3b8", fontWeight: 800 }}>
          {label}
        </Typography>
        <Typography sx={{ mt: 0.15, fontSize: 12.5, color: "#0f172a", fontWeight: 700, wordBreak: "break-word" }}>
          {value || "—"}
        </Typography>
      </Box>
    </Stack>
  );
}

function FilterSummaryTab({ active, label, onClick }) {
  return (
    <Button
      variant="text"
      onClick={onClick}
      sx={{
        minWidth: 0,
        px: 0,
        py: 0.75,
        borderRadius: 0,
        textTransform: "none",
        color: active ? "#2563eb" : "#64748b",
        fontWeight: active ? 900 : 700,
        borderBottom: `2px solid ${active ? "#2563eb" : "transparent"}`,
        "&:hover": {
          bgcolor: "transparent",
          color: "#2563eb",
        },
      }}
    >
      {label}
    </Button>
  );
}

const DeviceListPortalModal = ({ open = true, onClose, asPage = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.down("lg"));
  const currentUser = useSelector((state) => state.auth.user);
  const currentUserRole = normalizeRole(currentUser?.role);
  const deviceActions = getDeviceActionsEffective(currentUser);
  const { showStatus } = useStatus();
  const fileInputRef = useRef(null);
  const availableTabs = PRIMARY_TABS.filter((tab) => {
    if (tab.value === "deleted" && !deviceActions.canDelete) return false;
    if (tab.value === "renewal" && currentUserRole === ROLES.USER) return false;
    return true;
  });
  const [primaryTab, setPrimaryTab] = useState("devices");
  const includeDeleted = primaryTab === "deleted";

  const [summaryTab, setSummaryTab] = useState("all");
  const [filterBy, setFilterBy] = useState("imei");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [filterInputValue, setFilterInputValue] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [deviceDetailCache, setDeviceDetailCache] = useState({});
  const [cachedPageData, setCachedPageData] = useState(null);
  const [renewalFromDate, setRenewalFromDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().slice(0, 10);
  });
  const [renewalFromHour, setRenewalFromHour] = useState(0);
  const [renewalFromMin, setRenewalFromMin] = useState(0);
  const [renewalToDate, setRenewalToDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [renewalToHour, setRenewalToHour] = useState(23);
  const [renewalToMin, setRenewalToMin] = useState(59);
  const [renewalVehicleId, setRenewalVehicleId] = useState(null);
  const [renewalVehicleInput, setRenewalVehicleInput] = useState("");
  const [renewalQuick, setRenewalQuick] = useState("Today");
  const [renewalApplied, setRenewalApplied] = useState(null);
  const [expiringCategory, setExpiringCategory] = useState("7");
  const [expiringFromDate, setExpiringFromDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().slice(0, 10);
  });
  const [expiringToDate, setExpiringToDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [expiringQuick, setExpiringQuick] = useState("Week");
  const [expiringApplied, setExpiringApplied] = useState(null);
  const filterUsesDropdown = ["customer", "dealer", "status", "group"].includes(filterBy);
  const fetchLimit = rowsPerPage === -1 ? 0 : rowsPerPage;
  const trimmedSearchQuery = String(searchQuery || "").trim();

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(trimmedSearchQuery);
    }, filterUsesDropdown ? 0 : 400);
    return () => clearTimeout(timer);
  }, [filterUsesDropdown, trimmedSearchQuery]);

  const renewalQueryArgs = useMemo(() => {
    if (primaryTab !== "renewal") return null;
    const args = {
      page,
      limit: fetchLimit > 0 ? Math.min(fetchLimit, 100) : 0,
      all: rowsPerPage === -1 ? "true" : undefined,
    };
    if (renewalApplied?.fromTs instanceof Date && !Number.isNaN(renewalApplied.fromTs.getTime())) {
      args.from = renewalApplied.fromTs.toISOString();
    }
    if (renewalApplied?.toTs instanceof Date && !Number.isNaN(renewalApplied.toTs.getTime())) {
      args.to = renewalApplied.toTs.toISOString();
    }
    if (renewalApplied?.vehicleId) args.vehicleId = renewalApplied.vehicleId;
    return args;
  }, [fetchLimit, page, primaryTab, renewalApplied, rowsPerPage]);

  const expiringQueryArgs = useMemo(() => {
    if (primaryTab !== "expiring") return null;
    const args = {
      page,
      limit: fetchLimit > 0 ? Math.min(fetchLimit, 100) : 0,
      all: rowsPerPage === -1 ? "true" : undefined,
      category: expiringApplied?.category || expiringCategory,
    };
    if (expiringApplied?.fromTs instanceof Date && !Number.isNaN(expiringApplied.fromTs.getTime())) {
      args.from = expiringApplied.fromTs.toISOString();
    }
    if (expiringApplied?.toTs instanceof Date && !Number.isNaN(expiringApplied.toTs.getTime())) {
      args.to = expiringApplied.toTs.toISOString();
    }
    return args;
  }, [expiringApplied, expiringCategory, fetchLimit, page, primaryTab, rowsPerPage]);

  const devicesQueryArgs = useMemo(() => {
    if (primaryTab !== "devices" && primaryTab !== "deleted") return null;

    const args = {
      scopeUserId: String(currentUser?._id || currentUser?.id || ""),
      page,
      limit: fetchLimit > 0 ? Math.min(fetchLimit, 100) : 0,
      all: rowsPerPage === -1 ? "true" : undefined,
      includeDeleted: includeDeleted ? "true" : "false",
      summaryTab: primaryTab === "devices" ? summaryTab : undefined,
      status: includeDeleted ? "deleted" : undefined,
    };

    if (filterBy === "dealer" && debouncedSearchQuery) {
      args.dealer = debouncedSearchQuery;
    } else if (filterBy === "customer" && debouncedSearchQuery) {
      args.customer = debouncedSearchQuery;
    } else if (filterBy === "group" && debouncedSearchQuery) {
      args.group = debouncedSearchQuery;
    } else if (filterBy === "status" && debouncedSearchQuery) {
      args.status = debouncedSearchQuery;
    } else if (debouncedSearchQuery) {
      args.search = debouncedSearchQuery;
      args.filterBy = filterBy;
    }

    return args;
  }, [currentUser?._id, currentUser?.id, debouncedSearchQuery, fetchLimit, filterBy, includeDeleted, page, primaryTab, rowsPerPage, summaryTab]);

  const activeQueryArgs =
    primaryTab === "renewal"
      ? renewalQueryArgs
      : primaryTab === "expiring"
        ? expiringQueryArgs
        : devicesQueryArgs;

  const {
    data: devicesPageResponse,
    refetch: refetchDevicesPage,
    isFetching: devicesPageFetching,
    isLoading: devicesPageLoading,
    isError: devicesPageError,
  } = useGetDeviceListPageV2Query(
    primaryTab === "devices" || primaryTab === "deleted" ? devicesQueryArgs : skipToken,
    { refetchOnMountOrArgChange: true }
  );
  const {
    data: renewalsPageResponse,
    refetch: refetchRenewalsPage,
    isFetching: renewalsPageFetching,
    isLoading: renewalsPageLoading,
    isError: renewalsPageError,
  } = useGetDeviceRenewalsPageV2Query(primaryTab === "renewal" ? renewalQueryArgs : skipToken);
  const {
    data: expiringPageResponse,
    refetch: refetchExpiringPage,
    isFetching: expiringPageFetching,
    isLoading: expiringPageLoading,
    isError: expiringPageError,
  } = useGetDeviceExpiringPageV2Query(primaryTab === "expiring" ? expiringQueryArgs : skipToken);

  const livePageResponse =
    primaryTab === "renewal"
      ? renewalsPageResponse
      : primaryTab === "expiring"
        ? expiringPageResponse
        : devicesPageResponse;
  const pageData = livePageResponse || cachedPageData;
  const refetch =
    primaryTab === "renewal"
      ? refetchRenewalsPage
      : primaryTab === "expiring"
        ? refetchExpiringPage
        : refetchDevicesPage;
  const devicesLoading =
    primaryTab === "renewal"
      ? renewalsPageLoading
      : primaryTab === "expiring"
        ? expiringPageLoading
        : devicesPageLoading;
  const devicesFetching =
    primaryTab === "renewal"
      ? renewalsPageFetching
      : primaryTab === "expiring"
        ? expiringPageFetching
        : devicesPageFetching;
  const devicesError =
    primaryTab === "renewal"
      ? renewalsPageError
      : primaryTab === "expiring"
        ? expiringPageError
        : devicesPageError;
  const serverTotal = Number(pageData?.total || 0);
  const { data: assignableUsersPayload = [] } = useGetAssignableUsersQuery(["viewer", "driver", "client", "user", "group_admin"], {
    skip: !open,
  });
  const { data: allUsersPayload = [] } = useGetAllUsersQuery(undefined, {
    skip: !open,
  });
  const users = useMemo(
    () => (Array.isArray(assignableUsersPayload) ? assignableUsersPayload : []),
    [assignableUsersPayload]
  );
  const allUsers = useMemo(
    () => (Array.isArray(allUsersPayload) ? allUsersPayload : []),
    [allUsersPayload]
  );
  const { data: groupsPayload = [] } = useGetAllGroupsQuery();
  const [editDevice, { isLoading: updatingDevice }] = useEditDeviceMutation();
  const [deleteDevice] = useDeleteDeviceMutation();
  const [bulkDeleteDevices, { isLoading: bulkDeleting }] = useBulkDeleteDevicesMutation();
  const [restoreDevice] = useRestoreDeviceMutation();
  const [permanentlyDeleteDevice] = usePermanentlyDeleteDeviceMutation();
  const [revealUserPassword] = useRevealUserPasswordMutation();
  const [addDevicesToGroup] = useAddDevicesToGroupMutation();
  const [loadDeviceDetail] = useLazyGetDeviceV2ByIdQuery();

  useEffect(() => {
    const cachedPayload = readDeviceViewCache(DEVICE_LIST_MODAL_PAGE_CACHE_SCOPE, {
      userId: currentUser?._id,
      params: activeQueryArgs || {},
    });
    if (cachedPayload?.data && Array.isArray(cachedPayload.data)) {
      setCachedPageData(cachedPayload);
      return;
    }

    setCachedPageData(null);
  }, [activeQueryArgs, currentUser?._id]);

  useEffect(() => {
    const nextPayload = livePageResponse;
    if (!nextPayload?.data || !Array.isArray(nextPayload.data)) return;

    writeDeviceViewCache(DEVICE_LIST_MODAL_PAGE_CACHE_SCOPE, {
      userId: currentUser?._id,
      params: activeQueryArgs || {},
      payload: nextPayload,
    });
  }, [activeQueryArgs, currentUser?._id, livePageResponse]);

  const [modalMode, setModalMode] = useState("table");
  const [editingDevice, setEditingDevice] = useState(null);
  const [notificationDevice, setNotificationDevice] = useState(null);
  const [reportDevice, setReportDevice] = useState(null);
  const [certificateDevice, setCertificateDevice] = useState(null);
  const [sensorDevice, setSensorDevice] = useState(null);
  const [assignUserDevice, setAssignUserDevice] = useState(null);
  const [assignUserId, setAssignUserId] = useState("");
  const [assignGroupDevice, setAssignGroupDevice] = useState(null);
  const [assignGroupId, setAssignGroupId] = useState("");
  const [menuDevice, setMenuDevice] = useState(null);
  const [editingOdo, setEditingOdo] = useState(null);
  const [editingOdoValue, setEditingOdoValue] = useState("");
  const [selectedDeviceIds, setSelectedDeviceIds] = useState([]);
  const [selectedPanelDeviceId, setSelectedPanelDeviceId] = useState("");
  const [mobilePanelOpen, setMobilePanelOpen] = useState(false);
  const [extendValidityDevice, setExtendValidityDevice] = useState(null);
  const [extendValidityYears, setExtendValidityYears] = useState(1);
  const [extendValidityRemark, setExtendValidityRemark] = useState("");
  const [extendValidityFrom, setExtendValidityFrom] = useState("current");
  const [userInfoDialog, setUserInfoDialog] = useState(null);
  const [revealedPasswords, setRevealedPasswords] = useState({});
  const [loadingPasswordIds, setLoadingPasswordIds] = useState(new Set());
  const { data: fleetSummaryPayload } = useGetFleetSummaryQuery(undefined, { skip: !open, refetchOnMountOrArgChange: true, refetchOnFocus: true, refetchOnReconnect: true });

  const baseDevices = useMemo(() => {
    return Array.isArray(pageData?.data) ? pageData.data : [];
  }, [pageData]);
  const devices = useMemo(
    () =>
      baseDevices.map((device) => {
        const deviceId = String(device?._id || device?.id || "");
        return deviceDetailCache[deviceId]
          ? mergeLegacyDeviceDetail(device, deviceDetailCache[deviceId])
          : device;
      }),
    [baseDevices, deviceDetailCache]
  );
  const visibleDevices = useMemo(
    () =>
      devices.filter((device) =>
        canAccessDeviceForRole({
          device,
          currentUser,
          currentUserRole,
        })
      ),
    [currentUser, currentUserRole, devices]
  );
  const ensureDeviceDetail = async (device, forceFresh = false) => {
    const deviceId = String(device?._id || device?.id || "").trim();
    if (!deviceId) return device;
    if (!forceFresh && deviceDetailCache[deviceId]) {
      return mergeLegacyDeviceDetail(device, deviceDetailCache[deviceId]);
    }
    const detailed = await loadDeviceDetail(deviceId, !forceFresh).unwrap();
    if (detailed) {
      setDeviceDetailCache((prev) => ({ ...prev, [deviceId]: detailed }));
      return mergeLegacyDeviceDetail(device, detailed);
    }
    return device;
  };
  const groups = useMemo(() => {
    const rows = groupsPayload?.data || groupsPayload || [];
    return Array.isArray(rows) ? rows : [];
  }, [groupsPayload]);

  const userDirectoryById = useMemo(() => {
    const map = new Map();
    [...users, ...allUsers].forEach((user) => {
      const id = String(user?._id || user?.id || "").trim();
      if (!id || map.has(id)) return;
      map.set(id, user);
    });
    return map;
  }, [allUsers, users]);



  const applyRenewalQuick = (quick) => {
    const now = new Date();
    const todayStr = now.toISOString().slice(0, 10);
    if (quick === "Today") {
      setRenewalFromDate(todayStr);
      setRenewalToDate(todayStr);
    } else if (quick === "Week") {
      const s = new Date(now); s.setDate(s.getDate() - 7);
      setRenewalFromDate(s.toISOString().slice(0, 10));
      setRenewalToDate(todayStr);
    } else if (quick === "Month") {
      const s = new Date(now); s.setMonth(s.getMonth() - 1);
      setRenewalFromDate(s.toISOString().slice(0, 10));
      setRenewalToDate(todayStr);
    }
    setRenewalQuick(quick);
  };

  const applyExpiringQuick = (quick) => {
    const now = new Date();
    const todayStr = now.toISOString().slice(0, 10);
    if (quick === "Week") {
      const s = new Date(now); s.setDate(s.getDate() - 7);
      setExpiringFromDate(s.toISOString().slice(0, 10));
      setExpiringToDate(todayStr);
    } else if (quick === "Month") {
      const s = new Date(now); s.setMonth(s.getMonth() - 1);
      setExpiringFromDate(s.toISOString().slice(0, 10));
      setExpiringToDate(todayStr);
    }
    setExpiringQuick(quick);
  };

  const handleRenewalSearch = () => {
    const fromTs = new Date(
      `${renewalFromDate}T${String(renewalFromHour).padStart(2, "0")}:${String(renewalFromMin).padStart(2, "0")}:00`
    );
    const toTs = new Date(
      `${renewalToDate}T${String(renewalToHour).padStart(2, "0")}:${String(renewalToMin).padStart(2, "0")}:59`
    );
    setRenewalApplied({ fromTs, toTs, vehicleId: renewalVehicleId });
    setPage(1);
  };

  const handleExpiringSearch = () => {
    const fromTs = new Date(`${expiringFromDate}T00:00:00`);
    const toTs = new Date(`${expiringToDate}T23:59:59`);
    setExpiringApplied({ category: expiringCategory, fromTs, toTs });
    setPage(1);
  };


  const groupById = useMemo(() => {
    const entries = groups.map((group) => [String(group?._id || ""), group]);
    return new Map(entries.filter(([id]) => id));
  }, [groups]);
  const deviceGroupMembership = useMemo(() => {
    const membership = new Map();
    groups.forEach((group) => {
      const groupId = String(group?._id || "");
      const groupName = String(group?.name || "").trim();
      (Array.isArray(group?.devices) ? group.devices : []).forEach((deviceId) => {
        const normalizedDeviceId = String(deviceId?._id || deviceId || "").trim();
        if (normalizedDeviceId && !membership.has(normalizedDeviceId)) {
          membership.set(normalizedDeviceId, { groupId, groupName });
        }
      });
    });
    return membership;
  }, [groups]);

  const normalizedRows = useMemo(
    () =>
      visibleDevices.map((device) => {
        const licenseInfo = getLicenseInfo(device);
        const live = device?.liveData || device?.deviceLive || device?.liveSnapshot || {};
        const latestRenewal = device?.latestRenewal || null;
        const dealerEntriesResolved = getUniqueEntityEntriesWithDirectory([
          ...toArray(device?.dealer),
          ...toArray(device?.dealerOwner),
          ...toArray(device?.dealerName),
        ], userDirectoryById);
        const createdByEntries = getUniqueEntityEntriesWithDirectory([
          ...toArray(device?.createdBy),
          ...toArray(device?.adminId),
        ], userDirectoryById);
        const userEntries = getUniqueEntityEntriesWithDirectory([
          ...toArray(device?.assignedToUser),
          ...toArray(device?.assignedToUsers),
          ...toArray(device?.assignedToViewers),
          ...toArray(device?.ownerUser),
          ...toArray(device?.user),
          ...toArray(device?.customer),
          ...toArray(device?.assignedToUsername),
          ...toArray(device?.assignedToUserName),
          ...toArray(device?.userName),
          ...toArray(device?.customerName),
          ...toArray(device?.ownerUserName),
        ], userDirectoryById);
        const dealerNames =
          dealerEntriesResolved.map((entry) => entry.label).filter(Boolean).join(", ") ||
          String(device?.dealerName || "").trim() ||
          displayName(device?.dealer) ||
          displayName(userDirectoryById.get(String(device?.dealerOwner || "").trim())) ||
          "-";
        const userNames =
          userEntries.map((entry) => entry.label).filter(Boolean).join(", ") ||
          String(device?.assignedToUsername || "").trim() ||
          String(device?.assignedToUserName || "").trim() ||
          String(device?.userName || "").trim() ||
          String(device?.customerName || "").trim() ||
          displayName(userDirectoryById.get(String(device?.assignedToUser || "").trim())) ||
          displayName(userDirectoryById.get(String(device?.ownerUser || "").trim())) ||
          displayName(userDirectoryById.get(String(device?.user || "").trim())) ||
          displayName(userDirectoryById.get(String(device?.customer || "").trim())) ||
          "-";
        const rawGroupRef = device?.deviceGroup || device?.group;
        const rawGroupId = String(rawGroupRef?._id || device?.groupId || rawGroupRef || "").trim();
        const membershipGroup = deviceGroupMembership.get(String(device?._id || device?.id || "").trim());
        const resolvedGroupId = rawGroupId || membershipGroup?.groupId || "";
        const resolvedGroup = resolvedGroupId ? groupById.get(resolvedGroupId) : null;
        const groupName =
          device?.groupName ||
          resolvedGroup?.name ||
          membershipGroup?.groupName ||
          (rawGroupRef ? displayName(rawGroupRef) : "-");
        const createdAt = normalizeDate(device?.createdAt);
        const resolvedVehicleStatus = String(resolveVehicleStatus(device) || "no_data")
          .toLowerCase()
          .replace(/_/g, " ")
          .trim();
        const freshestTs = Math.max(
          toTimestampMs(device?.lastDataReceivedAt),
          toTimestampMs(live?.packetReceivedAt),
          toTimestampMs(live?.lastSocketUpdate),
          toTimestampMs(live?.packetTime),
          toTimestampMs(live?.timestamp),
          toTimestampMs(live?.serverTime),
          toTimestampMs(live?.lastUpdate)
        );
        const hasNeverReportedData =
          normalizeStatusBucket(resolvedVehicleStatus) === "no data" ||
          freshestTs <= 0;
        const effectiveStatus = hasNeverReportedData ? "no data" : resolvedVehicleStatus;
        const statusKey = normalizeStatusBucket(effectiveStatus) || "no data";
        const deleted =
          String(device?.status || "").toLowerCase() === "deleted" ||
          Boolean(device?.deletedAt) ||
          Boolean(device?.isDeleted) ||
          device?.deleted === true;
        const resolvedLocation =
          live?.address ||
          live?.currentAddress ||
          live?.location ||
          device?.address ||
          ([live?.latitude || live?.lat, live?.longitude || live?.lng].filter(Boolean).join(", ")) ||
          "-";

        return {
          raw: device,
          id: device?._id || device?.id,
          vehicle: device?.name || device?.vehicleNumber || device?.deviceId || "-",
          imei: device?.imei || device?.deviceId || "-",
          groupId: resolvedGroupId,
          groupName,
          statusKey,
          status: hasNeverReportedData ? "No Data" : getDisplayDeviceStatus(device, licenseInfo),
          lastUpdate: hasNeverReportedData ? "-" : formatDate(device?.lastDataReceivedAt || live?.timestamp, true),
          lastUpdateTs: hasNeverReportedData ? null : normalizeDate(device?.lastDataReceivedAt || live?.timestamp),
          location: resolvedLocation,
          dealerIds: dealerEntriesResolved.map((entry) => entry.id).filter(Boolean),
          dealers: dealerNames,
          dealerEntries: dealerEntriesResolved.map((entry) => ({
            ...entry,
            user: entry?.id ? userDirectoryById.get(String(entry.id)) || null : null,
          })),
          userIds: userEntries.map((entry) => entry.id).filter(Boolean),
          users: userNames,
          userEntries: userEntries.map((entry) => ({
            ...entry,
            user: entry?.id ? userDirectoryById.get(String(entry.id)) || null : null,
          })),
          createdBy:
            createdByEntries.map((entry) => entry.label).filter(Boolean).join(", ") ||
            String(device?.createdByName || "").trim() ||
            resolveCreatedByLabel(device?.createdBy) ||
            resolveCreatedByLabel(device?.adminId) ||
            resolveCreatedByLabel(device?.dealerOwner) ||
            "-",
          createdByEntry: createdByEntries.map((entry) => ({
            ...entry,
            user: entry?.id ? userDirectoryById.get(String(entry.id)) || null : null,
          })),
          simNo:
            device?.simNo ||
            device?.msdn ||
            device?.simNumber ||
            device?.sim ||
            device?.simCard?.MSDN ||
            device?.asset?.simCard?.MSDN ||
            "-",
          model: device?.model || "-",
          contact: device?.contact || device?.driverContact || device?.driver?.contactNumber || device?.contactNumber || "-",
          icon: device?.vehicleType || device?.deviceIcon || "Default",
          disabled: deleted ? "Yes" : "No",
          deleted,
          licenseExpiryTime: formatDate(licenseInfo.expiryDate),
          licenseExpiryTs: licenseInfo.expiresAt,
          creationTime: formatDate(device?.createdAt),
          creationTs: createdAt,
          todaysOdo: numberValue(device?.todaysOdo).toFixed(0),
          totalOdo: numberValue(device?.totalOdo).toFixed(2).replace(/\.00$/, ""),
          licenseType: licenseInfo.type,
          hasBeenRenewed: Boolean(latestRenewal?.renewedAt),
          renewedAt: normalizeDate(latestRenewal?.renewedAt),
          renewalPreviousExpiry: normalizeDate(latestRenewal?.previousExpiryDate),
          renewalNextExpiry: normalizeDate(latestRenewal?.nextExpiryDate),
          renewalRemarks: latestRenewal?.remarks || "",
          renewalFrom: latestRenewal?.renewFrom || "",
          remarks: latestRenewal?.remarks || extractRemarks(device),
          integration: String(device?.integrationEnabled ?? device?.integration ?? false),
          expiryDiffDays: device?.expiryDiffDays ?? licenseInfo.diffDays,
        };
      }),
    [deviceGroupMembership, groupById, userDirectoryById, visibleDevices]
  );

  const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => i);
  const MIN_OPTIONS = Array.from({ length: 60 }, (_, i) => i);

  const vehicleOptions = useMemo(
    () =>
      normalizedRows
        .filter((r) => !r.deleted)
        .map((r) => ({ value: r.id, label: `${r.vehicle} — ${r.imei}` })),
    [normalizedRows]
  );

  const summaryCounts = useMemo(
    () => {
      return {
        all: pageData?.counts?.all ?? serverTotal ?? normalizedRows.length,
        deleted: pageData?.counts?.deleted ?? null,
        noData: pageData?.counts?.noData ?? normalizedRows.filter((row) => !row.deleted && matchesSummaryBucket(row, "noData")).length,
        today: pageData?.counts?.today ?? null,
        month: pageData?.counts?.month ?? null,
        expiringToday: pageData?.counts?.expiringToday ?? null,
        expiringWeek: pageData?.counts?.expiringWeek ?? null,
        expiringMonth: pageData?.counts?.expiringMonth ?? null,
        expiring7: pageData?.counts?.expiring7 ?? null,
        expiring15: pageData?.counts?.expiring15 ?? null,
        expiring30: pageData?.counts?.expiring30 ?? null,
        expiring60: pageData?.counts?.expiring60 ?? null,
        expiredCount: pageData?.counts?.expiredCount ?? null,
      };
    },
    [normalizedRows, pageData?.counts, serverTotal]
  );

  const summaryPayloadCounts = useMemo(
    () => fleetSummaryPayload?.counts ?? EMPTY_COUNTS,
    [fleetSummaryPayload?.counts]
  );
  const activeRows = useMemo(() => normalizedRows.filter((row) => !row.deleted), [normalizedRows]);

  const dashboardSummary = useMemo(() => {
    const fallbackRunning = activeRows.filter((row) => normalizeStatusBucket(row.statusKey) === "running").length;
    const fallbackIdle = activeRows.filter((row) => normalizeStatusBucket(row.statusKey) === "idle").length;
    const fallbackStopped = activeRows.filter((row) => normalizeStatusBucket(row.statusKey) === "stopped").length;
    const fallbackNoReporting = activeRows.filter((row) => normalizeStatusBucket(row.statusKey) === "inactive").length;
    const fallbackExpired = activeRows.filter((row) => String(row.status || "").toLowerCase().includes("expired")).length;
    const fallbackNoData = activeRows.filter((row) => normalizeStatusBucket(row.statusKey) === "no data").length;

    const readCount = (...keys) => {
      for (const key of keys) {
        const value = summaryPayloadCounts?.[key];
        if (value !== undefined && value !== null) return Number(value) || 0;
      }
      return null;
    };

    return {
      all: readCount("all", "total") ?? summaryCounts.all ?? activeRows.length,
      running: readCount("running", "moving", "onlineDevices") ?? fallbackRunning,
      idle: readCount("idle", "idling") ?? fallbackIdle,
      stopped: readCount("stopped", "parked") ?? fallbackStopped,
      noReporting: readCount("noReporting", "inactive", "offline") ?? fallbackNoReporting,
      expired: readCount("expired", "expiredCount") ?? summaryCounts.expiredCount ?? fallbackExpired,
      noData: readCount("noData", "unknown") ?? summaryCounts.noData ?? fallbackNoData,
      addedToday: summaryCounts.today ?? activeRows.filter((row) => matchesSummaryBucket(row, "today")).length,
      addedMonth: summaryCounts.month ?? activeRows.filter((row) => matchesSummaryBucket(row, "month")).length,
      expiringToday: summaryCounts.expiringToday ?? activeRows.filter((row) => matchesSummaryBucket(row, "expiringToday")).length,
      expiringWeek: summaryCounts.expiringWeek ?? activeRows.filter((row) => matchesSummaryBucket(row, "expiringWeek")).length,
      expiringMonth: summaryCounts.expiringMonth ?? activeRows.filter((row) => matchesSummaryBucket(row, "expiringMonth")).length,
    };
  }, [activeRows, summaryCounts, summaryPayloadCounts]);

  const statusChartData = useMemo(
    () =>
      [
        { name: "Running", value: dashboardSummary.running },
        { name: "Idle", value: dashboardSummary.idle },
        { name: "Stopped", value: dashboardSummary.stopped },
        { name: "No Reporting", value: dashboardSummary.noReporting },
        { name: "Expired", value: dashboardSummary.expired },
        { name: "No Data", value: dashboardSummary.noData },
      ].filter((item) => Number(item.value) > 0),
    [dashboardSummary]
  );

  const licenseChartData = useMemo(() => {
    const sourceRows = activeRows;
    const expired = dashboardSummary.expired;
    const zeroToSeven = sourceRows.filter((row) => row.expiryDiffDays >= 0 && row.expiryDiffDays <= 7).length;
    const eightToThirty = sourceRows.filter((row) => row.expiryDiffDays >= 8 && row.expiryDiffDays <= 30).length;
    const moreThanThirty = sourceRows.filter((row) => row.expiryDiffDays > 30).length;
    return [
      { name: "Expired", value: expired, color: "#ef4444" },
      { name: "0-7 Days", value: zeroToSeven, color: "#f59e0b" },
      { name: "8-30 Days", value: eightToThirty, color: "#0ea5e9" },
      { name: "More than 30 Days", value: moreThanThirty, color: "#22c55e" },
    ];
  }, [activeRows, dashboardSummary.expired]);

  const addedTrendData = useMemo(() => {
    const today = START_OF_DAY(new Date());
    return Array.from({ length: 7 }, (_, index) => {
      const day = new Date(today);
      day.setDate(today.getDate() - (6 - index));
      const count = activeRows.filter((row) => isSameDay(row.creationTs, day)).length;
      return {
        label: shortDateLabel(day),
        value: count,
      };
    });
  }, [activeRows]);

  const renewalTrendData = useMemo(() => {
    const now = new Date();
    return Array.from({ length: 6 }, (_, index) => {
      const date = new Date(now.getFullYear(), now.getMonth() - (5 - index), 1);
      const count = activeRows.filter((row) => {
        const renewedAt = row.renewedAt;
        return (
          renewedAt instanceof Date &&
          !Number.isNaN(renewedAt.getTime()) &&
          renewedAt.getFullYear() === date.getFullYear() &&
          renewedAt.getMonth() === date.getMonth()
        );
      }).length;
      return {
        label: monthLabel(date),
        value: count,
      };
    });
  }, [activeRows]);

  const vehiclesAddedCurrentTotal = useMemo(
    () => addedTrendData.reduce((sum, item) => sum + Number(item.value || 0), 0),
    [addedTrendData]
  );

  const vehiclesAddedPreviousTotal = useMemo(() => {
    const today = START_OF_DAY(new Date());
    return Array.from({ length: 7 }, (_, index) => {
      const day = new Date(today);
      day.setDate(today.getDate() - (13 - index));
      return activeRows.filter((row) => isSameDay(row.creationTs, day)).length;
    }).reduce((sum, value) => sum + Number(value || 0), 0);
  }, [activeRows]);

  const renewalCurrentTotal = useMemo(
    () => renewalTrendData.reduce((sum, item) => sum + Number(item.value || 0), 0),
    [renewalTrendData]
  );

  const renewalPreviousTotal = useMemo(() => {
    const now = new Date();
    return Array.from({ length: 6 }, (_, index) => {
      const date = new Date(now.getFullYear(), now.getMonth() - (11 - index), 1);
      return activeRows.filter((row) => {
        const renewedAt = row.renewedAt;
        return (
          renewedAt instanceof Date &&
          !Number.isNaN(renewedAt.getTime()) &&
          renewedAt.getFullYear() === date.getFullYear() &&
          renewedAt.getMonth() === date.getMonth()
        );
      }).length;
    }).reduce((sum, value) => sum + Number(value || 0), 0);
  }, [activeRows]);

  const expiringSoonData = useMemo(() => {
    const zeroToSeven = activeRows.filter((row) => row.expiryDiffDays >= 0 && row.expiryDiffDays <= 7).length;
    const eightToFifteen = activeRows.filter((row) => row.expiryDiffDays >= 8 && row.expiryDiffDays <= 15).length;
    const sixteenToThirty = activeRows.filter((row) => row.expiryDiffDays >= 16 && row.expiryDiffDays <= 30).length;
    return [
      { name: "0 - 7 Days", value: zeroToSeven, color: "#8b5cf6" },
      { name: "8 - 15 Days", value: eightToFifteen, color: "#ff4d4f" },
      { name: "16 - 30 Days", value: sixteenToThirty, color: "#ffb020" },
    ];
  }, [activeRows]);

  const expiringSoonTotal = useMemo(
    () => expiringSoonData.reduce((sum, item) => sum + Number(item.value || 0), 0),
    [expiringSoonData]
  );

  const filterSelectOptions = useMemo(() => {
    if (filterBy === "customer") {
      const options = users
        .filter((user) => {
          const role = normalizeRole(user?.role);
          return role === ROLES.USER || role === ROLES.DRIVER || role === ROLES.CLIENT;
        })
        .map((user) => {
          const label = user?.username || user?.name || user?.mobileNo || user?.email || "User";
          return {
            value: String(user?._id || user?.id || label),
            label,
            searchText: [user?.username, user?.name, user?.mobileNo, user?.email, user?.role].filter(Boolean).join(" "),
          };
        });
      return Array.from(new Map(options.map((option) => [option.value, option])).values()).sort((a, b) => a.label.localeCompare(b.label));
    }

    if (filterBy === "dealer") {
      const options = users
        .filter((user) => {
          const role = normalizeRole(user?.role);
          return role === ROLES.DEALER || role === "group_admin";
        })
        .map((user) => {
          const label = user?.username || user?.name || user?.mobileNo || user?.email || "Dealer";
          return {
            value: String(user?._id || user?.id || label),
            label,
            searchText: [user?.username, user?.name, user?.mobileNo, user?.email, user?.role].filter(Boolean).join(" "),
          };
        });
      return Array.from(new Map(options.map((option) => [option.value, option])).values()).sort((a, b) => a.label.localeCompare(b.label));
    }

    if (filterBy === "group") {
      return Array.from(new Map(groups
        .map((group) => {
          const label = group?.name || "Group";
          return {
            value: String(group?._id || label),
            label,
            searchText: [group?.name, group?.description].filter(Boolean).join(" "),
          };
        })
        .map((option) => [option.value, option])).values()).sort((a, b) => a.label.localeCompare(b.label));
    }

    if (filterBy === "status") {
      return STATUS_FILTER_OPTIONS;
    }

    return [];
  }, [filterBy, groups, users]);

  const filteredRows = useMemo(() => {
    return normalizedRows;
  }, [normalizedRows]);

  const selectedFilterOption = useMemo(() => {
    if (!filterUsesDropdown || !searchQuery) return null;
    return (
      filterSelectOptions.find((option) => String(option.value) === String(searchQuery)) ||
      null
    );
  }, [filterSelectOptions, filterUsesDropdown, searchQuery]);

  // In server-paged mode the fetched `filteredRows` already IS the current page from server,
  // so skip client slicing and use server total for pagination / display range.
  const effectiveTotalCount = serverTotal || filteredRows.length;
  const totalPages = getTotalPagesForRows(effectiveTotalCount, rowsPerPage);
  const pagedRows = useMemo(() => {
    if (rowsPerPage === -1) {
      return filteredRows;
    }

    return filteredRows;
  }, [filteredRows, rowsPerPage]);
  const selectedPanelRow = useMemo(() => {
    if (!pagedRows.length) return null;
    return pagedRows.find((row) => String(row.id) === String(selectedPanelDeviceId)) || pagedRows[0] || null;
  }, [pagedRows, selectedPanelDeviceId]);
  const displayRange = useMemo(
    () => getDisplayRange(effectiveTotalCount, page, rowsPerPage),
    [effectiveTotalCount, page, rowsPerPage]
  );
  const showSelectionColumn = primaryTab !== "deleted" && deviceActions.canDelete;
  const selectedIdSet = useMemo(() => new Set(selectedDeviceIds), [selectedDeviceIds]);
  const selectablePageRows = useMemo(() => pagedRows.filter((row) => !row.deleted && row.id), [pagedRows]);
  const allPageSelected = selectablePageRows.length > 0 && selectablePageRows.every((row) => selectedIdSet.has(row.id));
  const somePageSelected = selectablePageRows.some((row) => selectedIdSet.has(row.id)) && !allPageSelected;

  const formatOdoCell = (value) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return "0";
    return numeric.toFixed(2).replace(/\.00$/, "");
  };

  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [page, totalPages]);

  useEffect(() => {
    if (!pagedRows.length) {
      setSelectedPanelDeviceId("");
      setMobilePanelOpen(false);
      return;
    }

    const stillVisible = pagedRows.some((row) => String(row.id) === String(selectedPanelDeviceId));
    if (!stillVisible) {
      setSelectedPanelDeviceId(String(pagedRows[0]?.id || ""));
    }
  }, [pagedRows, selectedPanelDeviceId]);


  useEffect(() => {
    setRenewalApplied(null);
    setExpiringApplied(null);
    setPage(1);
  }, [primaryTab]);


  useEffect(() => {
    setSelectedDeviceIds((prev) => {
      const next = prev.filter((id) =>
        normalizedRows.some((row) => row.id === id && !row.deleted)
      );

      if (next.length === prev.length && next.every((id, index) => id === prev[index])) {
        return prev;
      }

      return next;
    });
  }, [normalizedRows]);

  useEffect(() => {
    setSelectedDeviceIds([]);
  }, [primaryTab, searchQuery, filterBy, summaryTab]);

  const closeForm = () => {
    setEditingDevice(null);
    setModalMode("table");
  };

  const closeExtendValidityDialog = () => {
    setExtendValidityDevice(null);
    setExtendValidityYears(1);
    setExtendValidityRemark("");
    setExtendValidityFrom("current");
  };

  const openExtendValidityDialog = (row) => {
    setExtendValidityDevice(row?.raw || null);
    setExtendValidityYears(1);
    setExtendValidityRemark("");
    setExtendValidityFrom("current");
    setMenuDevice(null);
  };

  const handleCsvDownload = () => {
    const csv = buildCsv(filteredRows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "devices-list.csv";
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
    showStatus("success", "Devices CSV downloaded.");
  };

  const handleTemplateDownload = async () => {
    try {
      await downloadEntityTemplate("fleets");
      showStatus("success", "Fleet template downloaded.");
    } catch (error) {
      showStatus("error", error?.response?.data?.message || "Template download failed.");
    }
  };

  const handleImportDevices = async (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;
    try {
      const result = await uploadEntityFile("fleets", file, { validateOnly: false });
      await refetch();
      showStatus(
        "success",
        result?.message ||
          `Devices import completed (${result?.count || 0} imported${result?.skippedCount ? `, ${result.skippedCount} skipped` : ""}).`
      );
    } catch (error) {
      const payload = error?.response?.data;
      const details = Array.isArray(payload?.errors) ? payload.errors.slice(0, 4).join(" | ") : "";
      showStatus("error", `${payload?.message || "Devices import failed."}${details ? ` ${details}` : ""}`);
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const resetPrimaryFilters = () => {
    setFilterBy("imei");
    setSearchQuery("");
    setFilterInputValue("");
    setSummaryTab("all");
    setPage(1);
  };

  const handleRowFocus = (row) => {
    if (!row?.id) return;
    setSelectedPanelDeviceId(String(row.id));
    if (isMobile) setMobilePanelOpen(true);
  };

  const handleDelete = async (device) => {
    if (!device?.id) return;
    const confirmText = "Ye device deleted devices tab me move hoga. Continue?";
    if (!window.confirm(confirmText)) return;
    try {
      await deleteDevice(device.id).unwrap();
      setSelectedDeviceIds((prev) => prev.filter((id) => id !== device.id));
      showStatus("success", "Device moved to deleted devices.");
      setMenuDevice(null);
    } catch (error) {
      showStatus("error", error?.data?.msg || "Failed to delete device.");
    }
  };

  const toggleRowSelection = (deviceId) => {
    setSelectedDeviceIds((prev) =>
      prev.includes(deviceId) ? prev.filter((id) => id !== deviceId) : [...prev, deviceId]
    );
  };

  const toggleSelectCurrentPage = (checked) => {
    const pageIds = selectablePageRows.map((row) => row.id);
    setSelectedDeviceIds((prev) => {
      const next = new Set(prev);
      pageIds.forEach((id) => {
        if (checked) next.add(id);
        else next.delete(id);
      });
      return Array.from(next);
    });
  };

  const handleBulkDelete = async () => {
    if (!selectedDeviceIds.length) {
      showStatus("warning", "Please select at least one device.");
      return;
    }
    if (!window.confirm(`${selectedDeviceIds.length} device(s) ko deleted devices me move karna hai. Continue?`)) return;

    try {
      const response = await bulkDeleteDevices(selectedDeviceIds).unwrap();
      setSelectedDeviceIds([]);
      await refetch();
      showStatus("success", response?.msg || "Selected devices moved to deleted devices.");
    } catch (error) {
      showStatus("error", error?.data?.msg || "Failed to delete selected devices.");
    }
  };

  const handleRestore = async (device) => {
    if (!device?.id) return;
    if (!window.confirm("Is deleted device ko restore karna hai?")) return;
    try {
      await restoreDevice(device.id).unwrap();
      showStatus("success", "Device restored successfully.");
      setMenuDevice(null);
    } catch (error) {
      showStatus("error", error?.data?.msg || "Failed to restore device.");
    }
  };

  const handlePermanentDelete = async (device) => {
    if (!device?.id) return;
    if (!window.confirm("Ye device permanently DB se delete ho jayega. Continue?")) return;
    try {
      await permanentlyDeleteDevice(device.id).unwrap();
      showStatus("success", "Device permanently deleted.");
      setMenuDevice(null);
    } catch (error) {
      showStatus("error", error?.data?.msg || "Failed to permanently delete device.");
    }
  };

  const handleAssignUser = async () => {
    if (!assignUserDevice?.id) {
      showStatus("warning", "Please select a device.");
      return;
    }
    try {
      await editDevice({
        deviceId: assignUserDevice.id,
        assignedToUser: assignUserId || null,
      }).unwrap();
      showStatus("success", "Device user assignment updated.");
      setAssignUserDevice(null);
      setAssignUserId("");
    } catch (error) {
      showStatus("error", error?.data?.msg || "Failed to assign user.");
    }
  };

  const handleAssignGroup = async () => {
    if (!assignGroupDevice?.id || !assignGroupId) {
      showStatus("warning", "Please select a group.");
      return;
    }
    try {
      await addDevicesToGroup({ groupId: assignGroupId, deviceIds: [assignGroupDevice.id] }).unwrap();
      showStatus("success", "Device assigned to group.");
      setAssignGroupDevice(null);
      setAssignGroupId("");
    } catch (error) {
      showStatus("error", error?.data?.msg || "Failed to assign group.");
    }
  };

  const openMoreMenu = (_event, row) => {
    setMenuDevice(row);
  };

  const beginOdoEdit = (row, field) => {
    if (!row?.id) return;
    setEditingOdo({ deviceId: row.id, field });
    setEditingOdoValue(String(Number(row.raw?.[`${field}RawKm`] ?? row.raw?.[field] ?? 0) || 0));
  };

  const cancelOdoEdit = () => {
    setEditingOdo(null);
    setEditingOdoValue("");
  };

  const saveOdoEdit = async () => {
    if (!editingOdo?.deviceId || !editingOdo?.field) return;

    const numericValue = Math.max(0, Number(editingOdoValue) || 0);
    const payload =
      editingOdo.field === "todaysOdo"
        ? { deviceId: editingOdo.deviceId, todaysOdo: numericValue }
        : { deviceId: editingOdo.deviceId, totalOdo: numericValue };

    try {
      await editDevice(payload).unwrap();
      cancelOdoEdit();
      await refetch();
      showStatus("success", "Odometer updated.");
    } catch (error) {
      showStatus("error", error?.data?.msg || "Failed to update odometer.");
    }
  };

  const extendValidityBaseDate = useMemo(() => {
    if (extendValidityFrom === "original") {
      return (
        normalizeDate(
          extendValidityDevice?.licenseRuntime?.expiryDate ||
            extendValidityDevice?.deviceLicense?.expiryDate
        ) || new Date()
      );
    }
    return new Date();
  }, [extendValidityDevice, extendValidityFrom]);

  const extendValidityNextExpiry = useMemo(
    () => addYears(extendValidityBaseDate, extendValidityYears),
    [extendValidityBaseDate, extendValidityYears]
  );

  const handleExtendValidity = async () => {
    const deviceId = extendValidityDevice?._id || extendValidityDevice?.id;
    if (!deviceId) {
      showStatus("warning", "Please select a valid device.");
      return;
    }

    try {
      await editDevice({
        deviceId,
        "deviceLicense.licenseType": "yearly",
        "deviceLicense.validity": Number(extendValidityYears) * 12,
        "deviceLicense.startDate": extendValidityBaseDate,
        "deviceLicense.expiryDate": extendValidityNextExpiry,
        licenseRenewalAction: true,
        licenseRenewalRemark: extendValidityRemark,
        licenseRenewFrom: extendValidityFrom,
      }).unwrap();
      showStatus("success", "Device validity extended successfully.");
      closeExtendValidityDialog();
      await refetch();
    } catch (error) {
      showStatus("error", error?.data?.msg || "Failed to extend device validity.");
    }
  };

  const handleOpenUserInfoDialog = (title, entries = []) => {
    const normalizedEntries = (Array.isArray(entries) ? entries : [])
      .map((entry) => {
        const id = String(entry?.id || entry?._id || "").trim();
        const user = entry?.user || (id ? userDirectoryById.get(id) : null) || null;
        const summary = getEntitySummary(user || entry);
        return {
          id: id || summary.id,
          label: entry?.label || summary.name || "-",
          user,
          summary,
        };
      })
      .filter((entry) => entry.id || entry.label !== "-");

    if (!normalizedEntries.length) {
      showStatus("info", "No user information available.");
      return;
    }

    setUserInfoDialog({
      title,
      entries: normalizedEntries,
      selectedId: normalizedEntries[0]?.id || "",
    });
  };

  const selectedUserInfoEntry = userInfoDialog?.entries?.find(
    (entry) => String(entry.id) === String(userInfoDialog?.selectedId || "")
  ) || userInfoDialog?.entries?.[0] || null;

  const handleTogglePassword = async (userId) => {
    const targetId = String(userId || "").trim();
    if (!targetId) return;

    if (Object.prototype.hasOwnProperty.call(revealedPasswords, targetId)) {
      setRevealedPasswords((prev) => {
        const next = { ...prev };
        delete next[targetId];
        return next;
      });
      return;
    }

    setLoadingPasswordIds((prev) => new Set(prev).add(targetId));
    try {
      const response = await revealUserPassword(targetId).unwrap();
      setRevealedPasswords((prev) => ({
        ...prev,
        [targetId]: response?.password || "",
      }));
    } catch (error) {
      showStatus("error", error?.data?.msg || "Failed to reveal password.");
    } finally {
      setLoadingPasswordIds((prev) => {
        const next = new Set(prev);
        next.delete(targetId);
        return next;
      });
    }
  };

  const copyToClipboard = async (label, value) => {
    const text = String(value || "").trim();
    if (!text || text === "-") {
      showStatus("warning", `${label} not available.`);
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      showStatus("success", `${label} copied.`);
    } catch {
      showStatus("error", `Failed to copy ${label.toLowerCase()}.`);
    }
  };

  const renderUserLinkCell = (label, entries, title) => {
    if (!Array.isArray(entries) || !entries.length || label === "-") {
      return <Typography sx={{ fontWeight: 700, fontSize: 12.5 }}>{label}</Typography>;
    }

    return (
      <Button
        variant="text"
        onClick={() => handleOpenUserInfoDialog(title, entries)}
        sx={{
          p: 0,
          minWidth: 0,
          textTransform: "none",
          justifyContent: "flex-start",
          fontWeight: 700,
          fontSize: 12.5,
          color: "#2563eb",
          "&:hover": { bgcolor: "transparent", textDecoration: "underline" },
        }}
      >
        {label}
      </Button>
    );
  };

  const selectedLicenseMeta = licenseTone(selectedPanelRow?.expiryDiffDays);

  const buildDeviceActionItems = (targetRow) =>
    !targetRow
      ? []
      : targetRow.deleted
        ? [
            deviceActions.canDelete
              ? {
                  key: "restore",
                  title: "Restore Device",
                  helper: "Move device back to active devices",
                  tone: "green",
                  icon: <RestoreFromTrashOutlinedIcon fontSize="small" />,
                  onClick: () => {
                    setMenuDevice(null);
                    handleRestore(targetRow);
                  },
                }
              : null,
            deviceActions.canDelete
              ? {
                  key: "deleteForever",
                  title: "Delete Permanently",
                  helper: "Remove this device forever",
                  tone: "red",
                  danger: true,
                  icon: <DeleteForeverOutlinedIcon fontSize="small" />,
                  onClick: () => {
                    setMenuDevice(null);
                    handlePermanentDelete(targetRow);
                  },
                }
              : null,
          ].filter(Boolean)
        : [
            deviceActions.canEdit
              ? {
                  key: "edit",
                  title: "Edit Device",
                  helper: "Update vehicle and device details",
                  tone: "blue",
                  icon: <EditIcon fontSize="small" />,
                  onClick: async () => {
                    setMenuDevice(null);
                    setEditingDevice(await ensureDeviceDetail(targetRow.raw, true));
                    setModalMode("edit");
                  },
                }
              : null,
            {
              key: "notifications",
              title: "Notification Settings",
              helper: "Manage alerts for this device",
              tone: "purple",
              icon: <NotificationsActiveOutlinedIcon fontSize="small" />,
              onClick: async () => {
                setMenuDevice(null);
                setNotificationDevice(await ensureDeviceDetail(targetRow.raw));
              },
            },
            {
              key: "assignUser",
              title: "Assign User",
              helper: "Assign or reassign device owner",
              tone: "green",
              icon: <PersonAddAlt1OutlinedIcon fontSize="small" />,
              onClick: () => {
                setMenuDevice(null);
                setAssignUserDevice(targetRow);
                setAssignUserId(targetRow.raw?.assignedToUser?._id || targetRow.raw?.assignedToUser || "");
              },
            },
            {
              key: "assignSensor",
              title: "Assign Sensor",
              helper: "Link sensor with this device",
              tone: "blue",
              icon: <SensorsOutlinedIcon fontSize="small" />,
              onClick: async () => {
                setMenuDevice(null);
                setSensorDevice(targetRow.raw ? await ensureDeviceDetail(targetRow.raw) : null);
              },
            },
            currentUserRole !== ROLES.USER
              ? {
              key: "certificate",
              title: "Installation Certificate",
              helper: "Create installation certificate",
              tone: "slate",
              icon: <BadgeOutlinedIcon fontSize="small" />,
              onClick: async () => {
                setMenuDevice(null);
                setCertificateDevice(targetRow.raw ? await ensureDeviceDetail(targetRow.raw) : null);
              },
                }
              : null,
            {
              key: "assignGroup",
              title: "Assign Group",
              helper: "Move this device to a group",
              tone: "green",
              icon: <GroupAddRoundedIcon fontSize="small" />,
              onClick: () => {
                setMenuDevice(null);
                setAssignGroupDevice(targetRow);
              },
            },
            currentUserRole !== ROLES.USER
              ? {
              key: "extend",
              title: "Extend Validity",
              helper: "Renew device license validity",
              tone: "amber",
              icon: <AutorenewOutlinedIcon fontSize="small" />,
              onClick: async () => {
                setMenuDevice(null);
                await openExtendValidityDialog({ ...targetRow, raw: await ensureDeviceDetail(targetRow.raw) });
              },
                }
              : null,
            currentUserRole !== ROLES.USER
              ? {
              key: "renewal",
              title: "Renewal Report",
              helper: "Open renewal report for this device",
              tone: "blue",
              icon: <AssessmentOutlinedIcon fontSize="small" />,
              onClick: async () => {
                setMenuDevice(null);
                setReportDevice(await ensureDeviceDetail(targetRow.raw));
              },
                }
              : null,
            {
              key: "audit",
              title: "Audit Report",
              helper: "Inspect audit and report history",
              tone: "blue",
              icon: <AssignmentTurnedInRoundedIcon fontSize="small" />,
              onClick: async () => {
                setMenuDevice(null);
                setReportDevice(await ensureDeviceDetail(targetRow.raw));
              },
            },
            deviceActions.canDelete
              ? {
                  key: "delete",
                  title: "Delete Device",
                  helper: "Move device into deleted devices tab",
                  tone: "red",
                  danger: true,
                  icon: <DeleteOutlineRoundedIcon fontSize="small" />,
                  onClick: () => {
                    setMenuDevice(null);
                    handleDelete(targetRow);
                  },
                }
              : null,
          ].filter(Boolean);

  const quickActionItems = selectedPanelRow?.deleted
    ? [
        deviceActions.canDelete
          ? {
              key: "restore",
              title: "Restore Device",
              helper: "Bring this device back to active list",
              tone: "green",
              icon: <RestoreFromTrashOutlinedIcon fontSize="small" />,
              onClick: () => handleRestore(selectedPanelRow),
            }
          : null,
        deviceActions.canDelete
          ? {
              key: "permanentDelete",
              title: "Permanent Delete",
              helper: "Remove permanently from database",
              tone: "red",
              danger: true,
              icon: <DeleteForeverOutlinedIcon fontSize="small" />,
              onClick: () => handlePermanentDelete(selectedPanelRow),
            }
          : null,
      ].filter(Boolean)
    : [
        deviceActions.canEdit
          ? {
              key: "edit",
              title: "Edit Device",
              helper: "Update master device details",
              tone: "blue",
              icon: <EditIcon fontSize="small" />,
              onClick: async () => {
                setEditingDevice(await ensureDeviceDetail(selectedPanelRow.raw, true));
                setModalMode("edit");
                setMobilePanelOpen(false);
              },
            }
          : null,
        {
          key: "notification",
          title: "Notification",
          helper: "Alert preferences and notifications",
          tone: "purple",
          icon: <MailOutlineRoundedIcon fontSize="small" />,
          onClick: async () => {
            setNotificationDevice(await ensureDeviceDetail(selectedPanelRow.raw));
            setMobilePanelOpen(false);
          },
        },
        {
          key: "assignUser",
          title: "Assign User",
          helper: "Attach or change customer user",
          tone: "green",
          icon: <PersonRoundedIcon fontSize="small" />,
          onClick: () => {
            setAssignUserDevice(selectedPanelRow);
            setAssignUserId(selectedPanelRow.raw?.assignedToUser?._id || selectedPanelRow.raw?.assignedToUser || "");
            setMobilePanelOpen(false);
          },
        },
        {
          key: "assignSensor",
          title: "Assign Sensor",
          helper: "Link sensor hardware to this device",
          tone: "blue",
          icon: <BluetoothSearchingRoundedIcon fontSize="small" />,
          onClick: async () => {
            setSensorDevice(await ensureDeviceDetail(selectedPanelRow.raw));
            setMobilePanelOpen(false);
          },
        },
        currentUserRole !== ROLES.USER
          ? {
          key: "certificate",
          title: "Create Certificate",
          helper: "Generate installation certificate",
          tone: "blue",
          icon: <BadgeOutlinedIcon fontSize="small" />,
          onClick: async () => {
            setCertificateDevice(await ensureDeviceDetail(selectedPanelRow.raw));
            setMobilePanelOpen(false);
          },
            }
          : null,
        {
          key: "assignService",
          title: "Assign Service",
          helper: "Map service ownership quickly",
          tone: "green",
          icon: <DirectionsCarFilledOutlinedIcon fontSize="small" />,
          onClick: () => {
            setAssignUserDevice(selectedPanelRow);
            setAssignUserId(selectedPanelRow.raw?.assignedToUser?._id || selectedPanelRow.raw?.assignedToUser || "");
            setMobilePanelOpen(false);
          },
        },
        {
          key: "assignGroup",
          title: "Assign Group",
          helper: "Move this device to a group",
          tone: "green",
          icon: <GroupAddRoundedIcon fontSize="small" />,
          onClick: () => {
            setAssignGroupDevice(selectedPanelRow);
            setMobilePanelOpen(false);
          },
        },
        currentUserRole !== ROLES.USER
          ? {
          key: "extend",
          title: "Extend Validity",
          helper: "Renew device license validity",
          tone: "amber",
          icon: <AutorenewOutlinedIcon fontSize="small" />,
          onClick: async () => {
            await openExtendValidityDialog({ ...selectedPanelRow, raw: await ensureDeviceDetail(selectedPanelRow.raw) });
            setMobilePanelOpen(false);
          },
            }
          : null,
        currentUserRole !== ROLES.USER
          ? {
          key: "renewal",
          title: "Renewal Report",
          helper: "Open renewal report for this device",
          tone: "blue",
          icon: <AssessmentOutlinedIcon fontSize="small" />,
          onClick: async () => {
            setReportDevice(await ensureDeviceDetail(selectedPanelRow.raw));
            setMobilePanelOpen(false);
          },
            }
          : null,
        {
          key: "audit",
          title: "Audit Report",
          helper: "Inspect audit and report history",
          tone: "blue",
          icon: <AssignmentTurnedInRoundedIcon fontSize="small" />,
          onClick: async () => {
            setReportDevice(await ensureDeviceDetail(selectedPanelRow.raw));
            setMobilePanelOpen(false);
          },
        },
        deviceActions.canDelete
          ? {
              key: "delete",
              title: "Delete Device",
              helper: "Move device into deleted devices tab",
              tone: "red",
              danger: true,
              icon: <DeleteOutlineRoundedIcon fontSize="small" />,
              onClick: () => handleDelete(selectedPanelRow),
            }
          : null,
        {
          key: "more",
          title: "More Options",
          helper: "Open device action popup",
          tone: "slate",
          icon: <SettingsSuggestRoundedIcon fontSize="small" />,
          onClick: (event) => openMoreMenu(event, selectedPanelRow),
        },
      ].filter(Boolean);

  const selectedDevicePanel = selectedPanelRow ? (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2.5,
        border: "1px solid #e2e8f0",
        boxShadow: "0 18px 46px rgba(15, 23, 42, 0.08)",
        overflow: "hidden",
        bgcolor: "#fff",
      }}
    >
      <Box
        sx={{
          p: 1.75,
          background: "linear-gradient(135deg, #eff6ff 0%, #f8fbff 52%, #ffffff 100%)",
          borderBottom: "1px solid #e2e8f0",
        }}
      >
        <Stack direction="row" spacing={1.3} alignItems="flex-start">
          <Avatar sx={{ width: 42, height: 42, bgcolor: "#dbeafe", color: "#2563eb" }}>
            <DirectionsCarFilledOutlinedIcon />
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontSize: 16, fontWeight: 900, color: "#0f172a" }}>
              {selectedPanelRow.vehicle}
            </Typography>
            <Typography sx={{ mt: 0.2, fontSize: 11.75, color: "#475569", fontWeight: 700 }}>
              {selectedPanelRow.imei}
            </Typography>
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.95, flexWrap: "wrap" }}>
              <Chip
                size="small"
                icon={<CircleIcon sx={{ color: `${getStatusColor(selectedPanelRow.status)} !important`, fontSize: 10 }} />}
                label={selectedPanelRow.status}
                sx={{
                  bgcolor: alpha(getStatusColor(selectedPanelRow.status), 0.12),
                  color: getStatusColor(selectedPanelRow.status),
                  fontWeight: 800,
                  borderRadius: 999,
                  "& .MuiChip-label": { px: 1.1, fontSize: 11.5 },
                }}
              />
              <Chip
                size="small"
                label={selectedLicenseMeta.label}
                sx={{
                  bgcolor: selectedLicenseMeta.bg,
                  color: selectedLicenseMeta.color,
                  fontWeight: 800,
                  borderRadius: 999,
                  "& .MuiChip-label": { px: 1.1, fontSize: 11.5 },
                }}
              />
            </Stack>
          </Box>
        </Stack>
      </Box>

      <Box sx={{ p: 1.5 }}>
        <Stack spacing={1.25}>
          <DeviceInfoRow label="Vehicle Number" value={selectedPanelRow.vehicle} icon={<BadgeOutlinedIcon sx={{ fontSize: 16 }} />} />
          <DeviceInfoRow label="Model" value={selectedPanelRow.model} icon={<DeviceHubRoundedIcon sx={{ fontSize: 16 }} />} />
          <DeviceInfoRow label="Last Update" value={selectedPanelRow.lastUpdate} icon={<AccessTimeRoundedIcon sx={{ fontSize: 16 }} />} />
          <DeviceInfoRow label="Location" value={selectedPanelRow.location} icon={<InfoOutlinedIcon sx={{ fontSize: 16 }} />} />
          <DeviceInfoRow label="Odometer" value={`${formatOdoCell(selectedPanelRow.raw?.totalOdo ?? selectedPanelRow.totalOdo)} km`} icon={<StraightenRoundedIcon sx={{ fontSize: 16 }} />} />
          <DeviceInfoRow label="License Expiry" value={selectedPanelRow.licenseExpiryTime} icon={<CalendarMonthRoundedIcon sx={{ fontSize: 16 }} />} />
          <DeviceInfoRow label="Group / Customer" value={`${selectedPanelRow.groupName || "—"} / ${selectedPanelRow.users || "—"}`} icon={<ApartmentRoundedIcon sx={{ fontSize: 16 }} />} />
        </Stack>

        <Box sx={{ mt: 1.9 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 800, color: "#0f172a" }}>Quick Actions</Typography>
          <Box sx={{ mt: 1.2, display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 0.8 }}>
            {quickActionItems.map((action) => {
              const { key, ...tileProps } = action;
              return <DeviceActionTile key={key} {...tileProps} />;
            })}
          </Box>
        </Box>
      </Box>
    </Paper>
  ) : null;

  const OuterShell = asPage ? Box : Dialog;
  const outerShellProps = asPage
    ? {
        sx: {
          bgcolor: "#f8fbff",
          borderRadius: { xs: 0, md: 3 },
          minHeight: "calc(100dvh - 112px)",
          display: "flex",
          flexDirection: "column",
        },
      }
    : {
        open,
        onClose,
        fullScreen: isMobile,
        fullWidth: true,
        maxWidth: false,
        PaperProps: {
          sx: {
            borderRadius: { xs: 0, md: 4 },
            overflow: "hidden",
            width: { xs: "100%", md: "calc(100vw - 48px)" },
            maxWidth: { xs: "100%", md: 1800 },
            minHeight: { xs: "100dvh", md: "92vh" },
            height: { xs: "100dvh", md: "92vh" },
            bgcolor: "#f8fbff",
          },
        },
      };
  const InnerShell = asPage ? Box : DialogContent;
  const innerShellProps = asPage
    ? { sx: { p: 0, bgcolor: "#f8fbff", display: "flex", flexDirection: "column", minHeight: 0, flex: 1 } }
    : { sx: { p: 0, bgcolor: "#f8fbff", display: "flex", flexDirection: "column", minHeight: 0 } };

  return (
    <>
      <OuterShell {...outerShellProps}>
        <InnerShell {...innerShellProps}>
          {!asPage ? (
            <Box
              sx={{
                px: { xs: 2, md: 3 },
                py: { xs: 2, md: 2.5 },
                borderBottom: "1px solid #dbe7f5",
                bgcolor: "#fff",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 2,
              }}
            >
              <Stack direction="row" spacing={1.6} alignItems="flex-start">
                <Avatar sx={{ width: 52, height: 52, bgcolor: "#eef4ff", color: "#2563eb" }}>
                  <DirectionsCarFilledOutlinedIcon />
                </Avatar>
                <Box>
                  <Typography sx={{ fontSize: { xs: 24, md: 28 }, fontWeight: 900, color: "#0f172a" }}>
                    Vehicles
                  </Typography>
                </Box>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <IconButton
                  onClick={onClose}
                  sx={{
                    border: "1px solid #dbe7f5",
                    bgcolor: "#fff",
                    boxShadow: "0 8px 20px rgba(15, 23, 42, 0.06)",
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Stack>
            </Box>
          ) : null}

          {modalMode === "table" ? (
            <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", background: PAGE_BG }}>
              <Box sx={{ px: { xs: 2, md: 3 }, py: { xs: 2, md: 2.5 } }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 2, md: 2.5 },
                    borderRadius: 3,
                    border: "1px solid #e2e8f0",
                    boxShadow: "0 20px 54px rgba(15, 23, 42, 0.06)",
                    bgcolor: "#fff",
                  }}
                >
                  <Stack
                    direction={{ xs: "column", xl: "row" }}
                    spacing={1.5}
                    justifyContent="space-between"
                    alignItems={{ xs: "stretch", xl: "center" }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Avatar sx={{ width: 48, height: 48, bgcolor: "#eef4ff", color: "#2563eb" }}>
                        <DirectionsCarFilledOutlinedIcon />
                      </Avatar>
                      <Box>
                        <Typography sx={{ fontSize: { xs: 25, md: 28 }, fontWeight: 900, color: "#0f172a" }}>
                          Vehicles
                        </Typography>
                      </Box>
                    </Stack>

                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} flexWrap="wrap">
                      {deviceActions.canCreate && (
                        <Button
                          variant="contained"
                          startIcon={<AddIcon />}
                          onClick={() => {
                            setEditingDevice(null);
                            setModalMode("create");
                          }}
                          sx={{
                            minHeight: 44,
                            px: 2.2,
                            borderRadius: 2,
                            textTransform: "none",
                            fontWeight: 800,
                            bgcolor: "#2563eb",
                            boxShadow: "0 12px 24px rgba(37, 99, 235, 0.22)",
                          }}
                        >
                          Add New Device
                        </Button>
                      )}
                      {currentUserRole !== ROLES.USER && (
                        <Button
                          variant="outlined"
                          startIcon={<UploadIcon />}
                          onClick={() => fileInputRef.current?.click()}
                          sx={{ minHeight: 44, px: 2.2, borderRadius: 2, textTransform: "none", fontWeight: 800, borderColor: "#d7deea", color: "#0f172a" }}
                        >
                          Import Devices
                        </Button>
                      )}
                      <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={handleCsvDownload}
                        sx={{ minHeight: 44, px: 2.2, borderRadius: 2, textTransform: "none", fontWeight: 800, borderColor: "#d7deea", color: "#0f172a" }}
                      >
                        Export Report
                      </Button>
                      <IconButton
                        onClick={() => refetch()}
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: 2,
                          border: "1px solid #d7deea",
                          color: "#0f172a",
                          bgcolor: "#fff",
                        }}
                      >
                        <RefreshIcon />
                      </IconButton>
                      <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: "none" }} onChange={handleImportDevices} />
                    </Stack>
                  </Stack>
                </Paper>

                {SHOW_LEGACY_TOP_SUMMARY_CARDS ? (
                  <Box
                  sx={{
                    mt: 2.25,
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))", lg: "repeat(3, minmax(0, 1fr))" },
                    gap: 1.4,
                    displayPrint: "none",
                  }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: { xs: 1.6, md: 1.9 },
                      borderRadius: 3,
                      border: "1px solid #e2e8f0",
                      boxShadow: "0 18px 48px rgba(15, 23, 42, 0.05)",
                      bgcolor: "#fff",
                    }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1.5}>
                      <Box>
                        <Typography sx={{ fontSize: 16, fontWeight: 800, color: "#0f172a" }}>Vehicles Added</Typography>
                        <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mt: 2 }}>
                          <Typography sx={{ fontSize: { xs: 30, lg: 34 }, lineHeight: 1, fontWeight: 900, color: "#0f172a" }}>
                            {formatCompactCount(vehiclesAddedCurrentTotal)}
                          </Typography>
                          <Typography sx={{ fontSize: 16, fontWeight: 800, color: "#22c55e" }}>
                            {formatGrowth(vehiclesAddedCurrentTotal, vehiclesAddedPreviousTotal)}
                          </Typography>
                        </Stack>
                        <Typography sx={{ mt: 0.5, fontSize: 13.5, color: "#64748b", fontWeight: 600 }}>
                          vs previous 7 days
                        </Typography>
                      </Box>
                      <Chip label="Last 7 Days" sx={{ bgcolor: "#fff", border: "1px solid #e2e8f0", fontWeight: 700, color: "#64748b" }} />
                    </Stack>
                    <Box sx={{ mt: 1.5, height: 150 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={addedTrendData}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e8eef7" />
                          <XAxis dataKey="label" tick={{ fill: "#94a3b8", fontSize: 12 }} axisLine={false} tickLine={false} />
                          <YAxis tick={{ fill: "#94a3b8", fontSize: 12 }} axisLine={false} tickLine={false} allowDecimals={false} />
                          <RechartsTooltip />
                          <Line type="monotone" dataKey="value" stroke="#2563eb" strokeWidth={3} dot={{ r: 4, fill: "#2563eb", strokeWidth: 0 }} activeDot={{ r: 5 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    </Box>
                  </Paper>

                  <Paper
                    elevation={0}
                    sx={{
                      p: { xs: 1.6, md: 1.9 },
                      borderRadius: 3,
                      border: "1px solid #e2e8f0",
                      boxShadow: "0 18px 48px rgba(15, 23, 42, 0.05)",
                      bgcolor: "#fff",
                    }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1.5}>
                      <Box>
                        <Typography sx={{ fontSize: 16, fontWeight: 800, color: "#0f172a" }}>Renewals</Typography>
                        <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mt: 2 }}>
                          <Typography sx={{ fontSize: { xs: 30, lg: 34 }, lineHeight: 1, fontWeight: 900, color: "#0f172a" }}>
                            {formatCompactCount(renewalCurrentTotal)}
                          </Typography>
                          <Typography sx={{ fontSize: 16, fontWeight: 800, color: "#22c55e" }}>
                            {formatGrowth(renewalCurrentTotal, renewalPreviousTotal)}
                          </Typography>
                        </Stack>
                        <Typography sx={{ mt: 0.5, fontSize: 13.5, color: "#64748b", fontWeight: 600 }}>
                          vs previous 6 months
                        </Typography>
                      </Box>
                      <Chip label="Last 6 Months" sx={{ bgcolor: "#fff", border: "1px solid #e2e8f0", fontWeight: 700, color: "#64748b" }} />
                    </Stack>
                    <Box sx={{ mt: 1.5, height: 150 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={renewalTrendData} barCategoryGap="38%">
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e8eef7" />
                          <XAxis dataKey="label" tick={{ fill: "#94a3b8", fontSize: 12 }} axisLine={false} tickLine={false} />
                          <YAxis tick={{ fill: "#94a3b8", fontSize: 12 }} axisLine={false} tickLine={false} allowDecimals={false} />
                          <RechartsTooltip />
                          <Bar dataKey="value" fill="#22c55e" radius={[7, 7, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </Paper>

                  <Paper
                    elevation={0}
                    sx={{
                      p: { xs: 1.6, md: 1.9 },
                      borderRadius: 3,
                      border: "1px solid #e2e8f0",
                      boxShadow: "0 18px 48px rgba(15, 23, 42, 0.05)",
                      bgcolor: "#fff",
                    }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1.5}>
                      <Typography sx={{ fontSize: 16, fontWeight: 800, color: "#0f172a" }}>Expiring Soon</Typography>
                      <Chip label="Next 30 Days" sx={{ bgcolor: "#fff", border: "1px solid #e2e8f0", fontWeight: 700, color: "#64748b" }} />
                    </Stack>
                    <Box sx={{ mt: 1.2, display: "grid", gridTemplateColumns: { xs: "1fr", sm: "160px 1fr" }, gap: 1.2, alignItems: "center" }}>
                      <Box sx={{ height: 150 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie data={expiringSoonData} dataKey="value" nameKey="name" innerRadius={34} outerRadius={58} paddingAngle={2}>
                              {expiringSoonData.map((entry) => (
                                <Cell key={entry.name} fill={entry.color} />
                              ))}
                            </Pie>
                            <RechartsTooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      </Box>
                      <Box>
                        <Box sx={{ mb: 2 }}>
                          <Typography sx={{ fontSize: { xs: 30, lg: 34 }, lineHeight: 1, fontWeight: 900, color: "#0f172a" }}>
                            {formatCompactCount(expiringSoonTotal)}
                          </Typography>
                          <Typography sx={{ mt: 0.4, fontSize: 14, color: "#64748b", fontWeight: 600 }}>
                            Vehicles
                          </Typography>
                        </Box>
                        <Stack spacing={1.15}>
                          {expiringSoonData.map((item) => (
                            <Stack key={item.name} direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: item.color }} />
                                <Typography sx={{ fontSize: 14, color: "#64748b", fontWeight: 700 }}>{item.name}</Typography>
                              </Stack>
                              <Typography sx={{ fontSize: 14, color: "#0f172a", fontWeight: 800 }}>
                                {formatCompactCount(item.value)}
                              </Typography>
                            </Stack>
                          ))}
                        </Stack>
                      </Box>
                    </Box>
                  </Paper>
                  </Box>
                ) : null}

                <Paper
                  elevation={0}
                  sx={{
                    mt: 2.25,
                    borderRadius: 3,
                    border: "1px solid #dbe7f5",
                    boxShadow: "0 20px 50px rgba(15, 23, 42, 0.08)",
                    overflow: "hidden",
                    bgcolor: "#fff",
                  }}
                >
                  <Box sx={{ px: { xs: 1.25, md: 2 }, pt: 1.25, borderBottom: "1px solid #e2e8f0" }}>
                    <Tabs
                      value={primaryTab}
                      onChange={(_, value) => {
                        setPrimaryTab(value);
                        setPage(1);
                      }}
                      variant={isMobile ? "scrollable" : "standard"}
                      scrollButtons="auto"
                      sx={{
                        "& .MuiTab-root": {
                          textTransform: "none",
                          minHeight: 48,
                          fontWeight: 800,
                          color: "#64748b",
                        },
                        "& .Mui-selected": {
                          color: "#2563eb !important",
                        },
                        "& .MuiTabs-indicator": {
                          height: 3,
                          borderRadius: 999,
                          backgroundColor: "#2563eb",
                        },
                      }}
                    >
                      {availableTabs.map((tab) => (
                        <Tab key={tab.value} value={tab.value} label={tab.label} />
                      ))}
                    </Tabs>
                  </Box>

                  <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr",
                        gap: 1.5,
                        alignItems: "start",
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        {primaryTab === "devices" && (
                          <Box sx={{ mb: 1.75, display: "flex", gap: { xs: 1.25, md: 2 }, flexWrap: "wrap", borderBottom: "1px solid #eef2f7", pb: 0.2 }}>
                            {[
                              { value: "all", label: "All Vehicles", count: summaryCounts.all },
                              { value: "noData", label: "No Data", count: summaryCounts.noData },
                              { value: "today", label: "Added Today", count: summaryCounts.today },
                              { value: "month", label: "Added This Month", count: summaryCounts.month },
                              { value: "expiringToday", label: "Expiring Today", count: summaryCounts.expiringToday },
                              { value: "expiringWeek", label: "Expiring This Week", count: summaryCounts.expiringWeek },
                              { value: "expiringMonth", label: "Expiring This Month", count: summaryCounts.expiringMonth },
                            ].map((item) => (
                              <FilterSummaryTab
                                key={item.value}
                                label={`${item.label} (${item.count ?? 0})`}
                                onClick={() => {
                                  setSummaryTab(item.value);
                                  setPage(1);
                                }}
                                active={summaryTab === item.value}
                              />
                            ))}
                          </Box>
                        )}

                        <Paper
                          elevation={0}
                          sx={{
                            borderRadius: 2.5,
                            border: "1px solid #e9eef6",
                            bgcolor: "#fff",
                            boxShadow: "0 10px 26px rgba(15, 23, 42, 0.04)",
                            p: { xs: 1.25, md: 1.6 },
                          }}
                        >
                          {primaryTab === "renewal" ? (
                            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(6, minmax(0, 1fr)) auto auto" }, gap: 1.1 }}>
                              <TextField type="date" size="small" label="From Date" value={renewalFromDate} onChange={(e) => { setRenewalFromDate(e.target.value); setRenewalQuick("Custom Date"); }} sx={inputSx} inputProps={{ max: renewalToDate }} />
                              <TextField select size="small" label="From Hr" value={renewalFromHour} onChange={(e) => setRenewalFromHour(Number(e.target.value))} sx={inputSx}>{HOUR_OPTIONS.map((h) => <MenuItem key={h} value={h}>{h}</MenuItem>)}</TextField>
                              <TextField select size="small" label="From Min" value={renewalFromMin} onChange={(e) => setRenewalFromMin(Number(e.target.value))} sx={inputSx}>{MIN_OPTIONS.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}</TextField>
                              <TextField type="date" size="small" label="To Date" value={renewalToDate} onChange={(e) => { setRenewalToDate(e.target.value); setRenewalQuick("Custom Date"); }} sx={inputSx} inputProps={{ min: renewalFromDate }} />
                              <TextField select size="small" label="To Hr" value={renewalToHour} onChange={(e) => setRenewalToHour(Number(e.target.value))} sx={inputSx}>{HOUR_OPTIONS.map((h) => <MenuItem key={h} value={h}>{h}</MenuItem>)}</TextField>
                              <TextField select size="small" label="To Min" value={renewalToMin} onChange={(e) => setRenewalToMin(Number(e.target.value))} sx={inputSx}>{MIN_OPTIONS.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}</TextField>
                              <Autocomplete
                                size="small"
                                options={vehicleOptions}
                                value={vehicleOptions.find((v) => v.value === renewalVehicleId) || null}
                                inputValue={renewalVehicleInput}
                                onInputChange={(_, val, reason) => {
                                  if (reason !== "reset") setRenewalVehicleInput(val);
                                }}
                                onChange={(_, opt) => {
                                  setRenewalVehicleId(opt?.value || null);
                                  setRenewalVehicleInput(opt?.label || "");
                                }}
                                isOptionEqualToValue={(opt, val) => opt.value === val.value}
                                getOptionLabel={(opt) => opt?.label || ""}
                                clearOnBlur={false}
                                renderInput={(params) => <TextField {...params} label="Vehicle" placeholder="Select Vehicle" sx={inputSx} />}
                              />
                              <TextField select size="small" label="Quick Range" value={renewalQuick} onChange={(e) => applyRenewalQuick(e.target.value)} sx={inputSx}>
                                {["Today", "Week", "Month", "Custom Date"].map((opt) => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                              </TextField>
                              <Button variant="contained" onClick={handleRenewalSearch} sx={{ borderRadius: 2, textTransform: "none", fontWeight: 800, bgcolor: "#2563eb" }}>Search</Button>
                              <Button variant="outlined" onClick={handleCsvDownload} sx={{ borderRadius: 2, textTransform: "none", fontWeight: 800 }}>Download</Button>
                            </Box>
                          ) : primaryTab === "expiring" ? (
                            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(5, minmax(0, 1fr)) auto auto" }, gap: 1.1 }}>
                              <TextField select size="small" label="Category" value={expiringCategory} onChange={(e) => setExpiringCategory(e.target.value)} sx={inputSx}>
                                <MenuItem value="expired">Expired Devices</MenuItem>
                                <MenuItem value="7">Expiring in 7 Days</MenuItem>
                                <MenuItem value="15">Expiring in 15 Days</MenuItem>
                                <MenuItem value="30">Expiring in 30 Days</MenuItem>
                                <MenuItem value="60">Expiring in 60 Days</MenuItem>
                              </TextField>
                              <TextField type="date" size="small" label="From Date" value={expiringFromDate} onChange={(e) => { setExpiringFromDate(e.target.value); setExpiringQuick("Custom Date"); }} sx={inputSx} />
                              <TextField type="date" size="small" label="To Date" value={expiringToDate} onChange={(e) => { setExpiringToDate(e.target.value); setExpiringQuick("Custom Date"); }} sx={inputSx} />
                              <TextField select size="small" label="Quick Range" value={expiringQuick} onChange={(e) => applyExpiringQuick(e.target.value)} sx={inputSx}>
                                {["Week", "Month", "Custom Date"].map((opt) => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                              </TextField>
                              <Chip label={`7 Days (${summaryCounts.expiring7 ?? 0})`} sx={{ height: 40, justifyContent: "flex-start", fontWeight: 800, borderRadius: 2, bgcolor: "#fff7ed", color: "#9a3412" }} />
                              <Chip label={`30 Days (${summaryCounts.expiring30 ?? 0})`} sx={{ height: 40, justifyContent: "flex-start", fontWeight: 800, borderRadius: 2, bgcolor: "#eff6ff", color: "#1d4ed8" }} />
                              <Button variant="contained" onClick={handleExpiringSearch} sx={{ borderRadius: 2, textTransform: "none", fontWeight: 800, bgcolor: "#2563eb" }}>Search</Button>
                              <Button variant="outlined" onClick={handleCsvDownload} sx={{ borderRadius: 2, textTransform: "none", fontWeight: 800 }}>Download</Button>
                            </Box>
                          ) : (
                            <Box sx={{ overflowX: "auto", overflowY: "hidden", pb: 0.5 }}>
                              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(7, minmax(170px, 1fr)) 120px 120px", xl: "180px minmax(210px, 1fr) repeat(4, 180px) 120px 120px" }, gap: 1.1, minWidth: { xs: 1160, xl: "auto" } }}>
                                <TextField
                                  select
                                  size="small"
                                  label="Filter By"
                                  value={filterBy}
                                  onChange={(event) => {
                                    setFilterBy(event.target.value);
                                    setSearchQuery("");
                                    setFilterInputValue("");
                                    setPage(1);
                                  }}
                                  sx={inputSx}
                                >
                                  {FILTER_OPTIONS.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                      {option.label}
                                    </MenuItem>
                                  ))}
                                </TextField>

                                {filterUsesDropdown ? (
                                  <Autocomplete
                                    fullWidth
                                    size="small"
                                    options={filterSelectOptions}
                                    value={selectedFilterOption}
                                    inputValue={filterInputValue}
                                    onInputChange={(_, value, reason) => {
                                      if (reason === "reset" && selectedFilterOption?.label) {
                                        setFilterInputValue(selectedFilterOption.label);
                                        return;
                                      }
                                      setFilterInputValue(value);
                                    }}
                                    onChange={(_, option) => {
                                      setSearchQuery(option?.value || "");
                                      setFilterInputValue(option?.label || "");
                                      setPage(1);
                                    }}
                                    isOptionEqualToValue={(option, value) => String(option?.value) === String(value?.value)}
                                    getOptionLabel={(option) => option?.label || ""}
                                    filterOptions={(options, state) => {
                                      const query = normalizeFilterValue(state.inputValue);
                                      if (!query) return options;
                                      return options.filter((option) =>
                                        normalizeFilterValue([option?.label, option?.searchText, option?.value].filter(Boolean).join(" ")).includes(query)
                                      );
                                    }}
                                    clearOnBlur={false}
                                    disablePortal
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        label="Search"
                                        placeholder={`Search ${FILTER_OPTIONS.find((option) => option.value === filterBy)?.label || "option"}`}
                                        sx={inputSx}
                                      />
                                    )}
                                  />
                                ) : (
                                  <TextField
                                    size="small"
                                    label="Search"
                                    placeholder="Search"
                                    value={searchQuery}
                                    onChange={(event) => {
                                      setSearchQuery(event.target.value);
                                      setPage(1);
                                    }}
                                    sx={inputSx}
                                    InputProps={{ endAdornment: <SearchIcon sx={{ color: "#94a3b8" }} /> }}
                                  />
                                )}

                                <TextField
                                  select
                                  size="small"
                                  label="Status"
                                  value={filterBy === "status" ? searchQuery : ""}
                                  onChange={(event) => {
                                    setFilterBy("status");
                                    setSearchQuery(event.target.value);
                                    setFilterInputValue(STATUS_FILTER_OPTIONS.find((option) => option.value === event.target.value)?.label || "");
                                    setPage(1);
                                  }}
                                  sx={inputSx}
                                >
                                  <MenuItem value="">All Status</MenuItem>
                                  {STATUS_FILTER_OPTIONS.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                  ))}
                                </TextField>

                                <TextField
                                  select
                                  size="small"
                                  label="Dealer"
                                  value={filterBy === "dealer" ? searchQuery : ""}
                                  onChange={(event) => {
                                    setFilterBy("dealer");
                                    setSearchQuery(event.target.value);
                                    setFilterInputValue(filterSelectOptions.find((option) => String(option.value) === String(event.target.value))?.label || "");
                                    setPage(1);
                                  }}
                                  sx={inputSx}
                                >
                                  <MenuItem value="">All Dealers</MenuItem>
                                  {users
                                    .filter((user) => {
                                      const role = normalizeRole(user?.role);
                                      return role === ROLES.DEALER || role === "group_admin";
                                    })
                                    .map((user) => (
                                      <MenuItem key={user._id} value={user._id}>
                                        {user?.username || user?.name || user?.mobileNo || "Dealer"}
                                      </MenuItem>
                                    ))}
                                </TextField>

                                <TextField
                                  select
                                  size="small"
                                  label="Customer"
                                  value={filterBy === "customer" ? searchQuery : ""}
                                  onChange={(event) => {
                                    setFilterBy("customer");
                                    setSearchQuery(event.target.value);
                                    setFilterInputValue("");
                                    setPage(1);
                                  }}
                                  sx={inputSx}
                                >
                                  <MenuItem value="">All Customers</MenuItem>
                                  {users
                                    .filter((user) => {
                                      const role = normalizeRole(user?.role);
                                      return role === ROLES.USER || role === ROLES.DRIVER || role === ROLES.CLIENT;
                                    })
                                    .map((user) => (
                                      <MenuItem key={user._id} value={user._id}>
                                        {user?.username || user?.name || user?.mobileNo || "User"}
                                      </MenuItem>
                                    ))}
                                </TextField>

                                <TextField
                                  select
                                  size="small"
                                  label="Group"
                                  value={filterBy === "group" ? searchQuery : ""}
                                  onChange={(event) => {
                                    setFilterBy("group");
                                    setSearchQuery(event.target.value);
                                    setFilterInputValue("");
                                    setPage(1);
                                  }}
                                  sx={inputSx}
                                >
                                  <MenuItem value="">All Groups</MenuItem>
                                  {groups.map((group) => (
                                    <MenuItem key={group._id} value={group._id}>
                                      {group?.name || "Group"}
                                    </MenuItem>
                                  ))}
                                </TextField>

                                <Button
                                  variant="outlined"
                                  startIcon={<RestartAltRoundedIcon />}
                                  onClick={resetPrimaryFilters}
                                  sx={{ borderRadius: 2, textTransform: "none", fontWeight: 800, borderColor: "transparent", color: "#2563eb", bgcolor: "#fff", minHeight: 40 }}
                                >
                                  Clear
                                </Button>
                                <Button
                                  variant="contained"
                                  startIcon={<FilterAltOutlinedIcon />}
                                  onClick={() => setPage(1)}
                                  sx={{ borderRadius: 2, textTransform: "none", fontWeight: 800, bgcolor: "#2563eb", minHeight: 40 }}
                                >
                                  Filters
                                </Button>
                              </Box>
                            </Box>
                          )}

                          <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap" sx={{ mt: 1.2 }}>
                            {showSelectionColumn && selectedDeviceIds.length > 0 && (
                              <Button
                                variant="contained"
                                color="error"
                                startIcon={<DeleteSweepRoundedIcon />}
                                onClick={handleBulkDelete}
                                disabled={bulkDeleting}
                                sx={{ textTransform: "none", borderRadius: 2, fontWeight: 800 }}
                              >
                                {bulkDeleting ? "Deleting..." : `Bulk Delete (${selectedDeviceIds.length})`}
                              </Button>
                            )}
                            {currentUserRole !== ROLES.USER && (
                              <Button
                                variant="outlined"
                                startIcon={<FileDownloadDoneRoundedIcon />}
                                onClick={handleTemplateDownload}
                                sx={{ textTransform: "none", borderRadius: 2, fontWeight: 800 }}
                              >
                                Import Template
                              </Button>
                            )}
                          </Stack>
                        </Paper>

                        <Paper
                          elevation={0}
                          sx={{
                            mt: 1.5,
                            borderRadius: 2.5,
                            border: "1px solid #e2e8f0",
                            overflow: "hidden",
                            bgcolor: "#fff",
                          }}
                        >
                          {devicesFetching ? <Box sx={{ height: 3, bgcolor: "#dbeafe" }}><Box sx={{ height: "100%", width: "45%", bgcolor: "#2563eb", animation: "pulse 1.4s ease-in-out infinite" }} /></Box> : null}

                          {!isMobile ? (
                            <TableContainer sx={{ maxHeight: "58vh", overflowX: "auto", width: "100%" }}>
                              <Table
                                stickyHeader
                                size="small"
                                sx={{
                                  width: "100%",
                                  minWidth: showSelectionColumn ? 1880 : 1820,
                                  borderCollapse: "separate",
                                  borderSpacing: 0,
                                  "& .MuiTableCell-head": {
                                    fontSize: 11.5,
                                    borderRight: "1px solid #dbe7f5",
                                    borderBottom: "1px solid #dbe7f5",
                                  },
                                  "& .MuiTableCell-body": {
                                    fontSize: 11.75,
                                    borderRight: "1px solid #e2e8f0",
                                    borderBottom: "1px solid #e2e8f0",
                                    verticalAlign: "top",
                                  },
                                }}
                              >
                                <TableHead>
                                  <TableRow>
                                    {showSelectionColumn && (
                                      <TableCell sx={{ ...stickySx(0, 60, 6), fontWeight: 800, color: "#64748b", textAlign: "center", bgcolor: "#f8fafc" }}>
                                        <Checkbox
                                          checked={allPageSelected}
                                          indeterminate={somePageSelected}
                                          onChange={(event) => toggleSelectCurrentPage(event.target.checked)}
                                          size="small"
                                        />
                                      </TableCell>
                                    )}
                                    <TableCell sx={{ ...stickySx(showSelectionColumn ? 60 : 0, 220, 5), fontWeight: 800, color: "#64748b", bgcolor: "#f8fafc" }}>Vehicle / IMEI</TableCell>
                                    <TableCell sx={{ minWidth: 140, fontWeight: 800, color: "#64748b", bgcolor: "#f8fafc" }}>Vehicle No.</TableCell>
                                    <TableCell sx={{ minWidth: 140, fontWeight: 800, color: "#64748b", bgcolor: "#f8fafc" }}>Status</TableCell>
                                    <TableCell sx={{ minWidth: 170, fontWeight: 800, color: "#64748b", bgcolor: "#f8fafc" }}>Last Update</TableCell>
                                    <TableCell sx={{ minWidth: 220, fontWeight: 800, color: "#64748b", bgcolor: "#f8fafc" }}>Location</TableCell>
                                    <TableCell sx={{ minWidth: 160, fontWeight: 800, color: "#64748b", bgcolor: "#f8fafc" }}>Created By</TableCell>
                                    <TableCell sx={{ minWidth: 170, fontWeight: 800, color: "#64748b", bgcolor: "#f8fafc" }}>User</TableCell>
                                    <TableCell sx={{ minWidth: 160, fontWeight: 800, color: "#64748b", bgcolor: "#f8fafc" }}>Sim Number</TableCell>
                                    <TableCell sx={{ minWidth: 150, fontWeight: 800, color: "#64748b", bgcolor: "#f8fafc" }}>Contact Number</TableCell>
                                    <TableCell sx={{ minWidth: 170, fontWeight: 800, color: "#64748b", bgcolor: "#f8fafc" }}>Creation Time</TableCell>
                                    <TableCell sx={{ minWidth: 170, fontWeight: 800, color: "#64748b", bgcolor: "#f8fafc" }}>License Expiry</TableCell>
                                    <TableCell sx={{ minWidth: 120, fontWeight: 800, color: "#64748b", bgcolor: "#f8fafc" }}>Model</TableCell>
                                    <TableCell sx={{ minWidth: 140, fontWeight: 800, color: "#64748b", bgcolor: "#f8fafc" }}>Group</TableCell>
                                    <TableCell sx={{ minWidth: 150, fontWeight: 800, color: "#64748b", bgcolor: "#f8fafc" }}>Customer</TableCell>
                                    <TableCell sx={{ minWidth: 140, fontWeight: 800, color: "#64748b", bgcolor: "#f8fafc" }}>Dealer</TableCell>
                                    <TableCell sx={{ minWidth: 110, fontWeight: 800, color: "#64748b", bgcolor: "#f8fafc" }}>Integration</TableCell>
                                    <TableCell sx={{ minWidth: 170, fontWeight: 800, color: "#64748b", bgcolor: "#f8fafc" }}>Odometer</TableCell>
                                    <TableCell sx={{ minWidth: 86, width: 86, fontWeight: 800, color: "#64748b", bgcolor: "#f8fafc" }}>Actions</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {pagedRows.length ? (
                                    pagedRows.map((row) => (
                                      <TableRow
                                        key={row.id}
                                        hover
                                        onClick={() => handleRowFocus(row)}
                                        selected={String(selectedPanelRow?.id) === String(row.id)}
                                        sx={{
                                          cursor: "pointer",
                                          "&:last-child .MuiTableCell-root": {
                                            borderBottom: "1px solid #dbe7f5",
                                          },
                                          "& .MuiTableCell-root": {
                                            bgcolor: String(selectedPanelRow?.id) === String(row.id) ? SELECTED_TABLE_ROW_BG : "#fff",
                                          },
                                        }}
                                      >
                                        {showSelectionColumn && (
                                          <TableCell sx={{ ...stickySx(0, 60, 5), textAlign: "center", bgcolor: String(selectedPanelRow?.id) === String(row.id) ? SELECTED_TABLE_ROW_BG : "#fff" }} onClick={(event) => event.stopPropagation()}>
                                            {!row.deleted ? (
                                              <Checkbox
                                                checked={selectedIdSet.has(row.id)}
                                                onChange={() => toggleRowSelection(row.id)}
                                                size="small"
                                              />
                                            ) : null}
                                          </TableCell>
                                        )}
                                        <TableCell sx={{ ...stickySx(showSelectionColumn ? 60 : 0, 220, 4), bgcolor: String(selectedPanelRow?.id) === String(row.id) ? SELECTED_TABLE_ROW_BG : "#fff" }}>
                                          <Typography sx={{ fontWeight: 800, color: "#0f172a" }}>{row.vehicle}</Typography>
                                          <Typography sx={{ mt: 0.25, fontSize: 12.5, color: "#64748b", fontWeight: 700 }}>{row.imei}</Typography>
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>{row.vehicle}</TableCell>
                                        <TableCell>
                                          <Chip
                                            size="small"
                                            icon={<CircleIcon sx={{ color: `${getStatusColor(row.status)} !important`, fontSize: 10 }} />}
                                            label={row.status}
                                            sx={{ bgcolor: alpha(getStatusColor(row.status), 0.12), color: getStatusColor(row.status), fontWeight: 800 }}
                                          />
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>{row.lastUpdate}</TableCell>
                                        <TableCell sx={{ fontWeight: 700, color: "#334155" }}>{row.location}</TableCell>
                                        <TableCell>{renderUserLinkCell(row.createdBy, row.createdByEntry, "Created By")}</TableCell>
                                        <TableCell>{renderUserLinkCell(row.users, row.userEntries, "Users")}</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>{row.simNo}</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>{row.contact}</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>{row.creationTime}</TableCell>
                                        <TableCell>
                                          <Chip
                                            size="small"
                                            label={licenseTone(row.expiryDiffDays).label}
                                            sx={{ bgcolor: licenseTone(row.expiryDiffDays).bg, color: licenseTone(row.expiryDiffDays).color, fontWeight: 800 }}
                                          />
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>{row.model}</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>{row.groupName}</TableCell>
                                        <TableCell>{renderUserLinkCell(row.users, row.userEntries, "Users")}</TableCell>
                                        <TableCell>{renderUserLinkCell(row.dealers, row.dealerEntries, "Dealers")}</TableCell>
                                        <TableCell>
                                          <Chip
                                            size="small"
                                            label={String(row.integration).toLowerCase() === "true" ? "Enabled" : "Disabled"}
                                            sx={{
                                              bgcolor: String(row.integration).toLowerCase() === "true" ? "#ecfdf5" : "#f8fafc",
                                              color: String(row.integration).toLowerCase() === "true" ? "#166534" : "#64748b",
                                              fontWeight: 800,
                                            }}
                                          />
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }} onClick={(event) => event.stopPropagation()}>
                                          <Stack spacing={0.8}>
                                            <Stack direction="row" spacing={0.5} alignItems="center">
                                              <Typography sx={{ fontWeight: 700, fontSize: 12.5, color: "#64748b", minWidth: 36 }}>Today</Typography>
                                              {editingOdo?.deviceId === row.id && editingOdo?.field === "todaysOdo" ? (
                                                <Stack direction="row" spacing={0.75} alignItems="center">
                                                  <TextField size="small" type="number" value={editingOdoValue} onChange={(event) => setEditingOdoValue(event.target.value)} inputProps={{ min: 0, step: 0.01 }} sx={{ width: 88, ...inputSx }} />
                                                  <Button size="small" variant="contained" onClick={saveOdoEdit}>Save</Button>
                                                  <Button size="small" variant="outlined" onClick={cancelOdoEdit}>Cancel</Button>
                                                </Stack>
                                              ) : (
                                                <Stack direction="row" spacing={0.5} alignItems="center">
                                                  <Typography sx={{ fontWeight: 800 }}>{formatOdoCell(row.raw?.todaysOdo ?? row.todaysOdo)} km</Typography>
                                                  {deviceActions.canEdit && <IconButton size="small" color="primary" onClick={() => beginOdoEdit(row, "todaysOdo")}><EditIcon sx={{ fontSize: 16 }} /></IconButton>}
                                                </Stack>
                                              )}
                                            </Stack>
                                            <Stack direction="row" spacing={0.5} alignItems="center">
                                              <Typography sx={{ fontWeight: 700, fontSize: 12.5, color: "#64748b", minWidth: 36 }}>Total</Typography>
                                              {editingOdo?.deviceId === row.id && editingOdo?.field === "totalOdo" ? (
                                                <Stack direction="row" spacing={0.75} alignItems="center">
                                                  <TextField size="small" type="number" value={editingOdoValue} onChange={(event) => setEditingOdoValue(event.target.value)} inputProps={{ min: 0, step: 0.01 }} sx={{ width: 88, ...inputSx }} />
                                                  <Button size="small" variant="contained" onClick={saveOdoEdit}>Save</Button>
                                                  <Button size="small" variant="outlined" onClick={cancelOdoEdit}>Cancel</Button>
                                                </Stack>
                                              ) : (
                                                <Stack direction="row" spacing={0.5} alignItems="center">
                                                  <Typography sx={{ fontWeight: 800 }}>{formatOdoCell(row.raw?.totalOdo ?? row.totalOdo)} km</Typography>
                                                  {deviceActions.canEdit && <IconButton size="small" color="primary" onClick={() => beginOdoEdit(row, "totalOdo")}><EditIcon sx={{ fontSize: 16 }} /></IconButton>}
                                                </Stack>
                                              )}
                                            </Stack>
                                          </Stack>
                                        </TableCell>
                                        <TableCell onClick={(event) => event.stopPropagation()} sx={{ textAlign: "center", whiteSpace: "nowrap" }}>
                                          <Tooltip title="Device Actions">
                                            <IconButton
                                              size="small"
                                              onClick={(event) => openMoreMenu(event, row)}
                                              sx={{
                                                color: "#111827",
                                                border: "1px solid #dbe7f5",
                                                borderRadius: 1.75,
                                                bgcolor: "#fff",
                                              }}
                                            >
                                              <MoreHorizIcon sx={{ fontSize: 19 }} />
                                            </IconButton>
                                          </Tooltip>
                                        </TableCell>
                                      </TableRow>
                                    ))
                                  ) : (
                                    <TableRow>
                                      <TableCell colSpan={showSelectionColumn ? 19 : 18} align="center" sx={{ py: 6, color: "#6b7280" }}>
                                        {devicesLoading || devicesFetching
                                          ? `Loading devices${devices.length ? ` (${devices.length} so far)` : ""}…`
                                          : devicesError
                                            ? "Failed to load devices. Please retry."
                                            : "No devices found."}
                                      </TableCell>
                                    </TableRow>
                                  )}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          ) : (
                            <Box sx={{ p: 1.1 }}>
                              <Stack spacing={1.15}>
                                {pagedRows.length ? (
                                  pagedRows.map((row) => (
                                    <Paper
                                      key={row.id}
                                      elevation={0}
                                      onClick={() => handleRowFocus(row)}
                                      sx={{
                                        p: 1.45,
                                        borderRadius: 2.5,
                                        border: `1px solid ${String(selectedPanelRow?.id) === String(row.id) ? "#bfdbfe" : "#e2e8f0"}`,
                                        bgcolor: String(selectedPanelRow?.id) === String(row.id) ? "#eff6ff" : "#fff",
                                      }}
                                    >
                                      <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
                                        <Box sx={{ minWidth: 0, flex: 1 }}>
                                          <Typography sx={{ fontSize: 15.5, fontWeight: 900, color: "#0f172a" }}>{row.vehicle}</Typography>
                                          <Typography sx={{ mt: 0.25, fontSize: 12.5, color: "#64748b", fontWeight: 700 }}>{row.imei}</Typography>
                                          <Stack direction="row" spacing={0.8} sx={{ mt: 1, flexWrap: "wrap" }}>
                                            <Chip size="small" label={row.status} sx={{ bgcolor: alpha(getStatusColor(row.status), 0.12), color: getStatusColor(row.status), fontWeight: 800 }} />
                                            <Chip size="small" label={licenseTone(row.expiryDiffDays).label} sx={{ bgcolor: licenseTone(row.expiryDiffDays).bg, color: licenseTone(row.expiryDiffDays).color, fontWeight: 800 }} />
                                          </Stack>
                                        </Box>
                                        {showSelectionColumn && !row.deleted ? (
                                          <Checkbox checked={selectedIdSet.has(row.id)} onChange={() => toggleRowSelection(row.id)} onClick={(event) => event.stopPropagation()} />
                                        ) : null}
                                      </Stack>

                                      <Box sx={{ mt: 1.2, display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 1 }}>
                                        <DeviceInfoRow label="Last Update" value={row.lastUpdate} icon={<AccessTimeRoundedIcon sx={{ fontSize: 16 }} />} />
                                        <DeviceInfoRow label="Location" value={row.location} icon={<InfoOutlinedIcon sx={{ fontSize: 16 }} />} />
                                        <DeviceInfoRow label="Model" value={row.model} icon={<DeviceHubRoundedIcon sx={{ fontSize: 16 }} />} />
                                        <DeviceInfoRow label="Group" value={row.groupName} icon={<ApartmentRoundedIcon sx={{ fontSize: 16 }} />} />
                                        <DeviceInfoRow label="Customer" value={row.users} icon={<PersonRoundedIcon sx={{ fontSize: 16 }} />} />
                                      </Box>
                                    </Paper>
                                  ))
                                ) : (
                                  <Typography sx={{ py: 5, textAlign: "center", color: "#6b7280", fontWeight: 700 }}>
                                    {devicesLoading || devicesFetching ? "Loading devices…" : devicesError ? "Failed to load devices. Please retry." : "No devices found."}
                                  </Typography>
                                )}
                              </Stack>
                            </Box>
                          )}

                          <Box sx={{ px: 2, py: 1.5, borderTop: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1.25 }}>
                            <Typography sx={{ color: "#475569", fontWeight: 700, fontSize: 13.5 }}>
                              Showing {displayRange.from} to {displayRange.to} of {effectiveTotalCount} entries
                            </Typography>
                            <Stack direction="row" spacing={1.25} alignItems="center">
                              <FormControl size="small" sx={{ minWidth: 96 }}>
                                <Select
                                  value={rowsPerPage}
                                  onChange={(event) => { setRowsPerPage(Number(event.target.value)); setPage(1); }}
                                  MenuProps={ROWS_PER_PAGE_MENU_PROPS}
                                  sx={inputSx}
                                >
                                  {CUSTOM_ROWS_PER_PAGE_OPTIONS.map((size) => (
                                    <MenuItem key={size} value={size}>
                                      {getRowsPerPageLabel(size)}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                              <Pagination page={page} count={totalPages} onChange={(_, value) => setPage(value)} shape="rounded" />
                            </Stack>
                          </Box>
                        </Paper>
                      </Box>

                    </Box>

                    <Box
                      sx={{
                        mt: 2.1,
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))", lg: "repeat(3, minmax(0, 1fr))" },
                        gap: 1.25,
                      }}
                    >
                      <Paper
                        elevation={0}
                        sx={{
                          p: { xs: 1.45, md: 1.7 },
                          borderRadius: 2.5,
                          border: "1px solid #e2e8f0",
                          boxShadow: "0 12px 30px rgba(15, 23, 42, 0.05)",
                          bgcolor: "#fff",
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1.25}>
                          <Box>
                            <Typography sx={{ fontSize: 14.5, fontWeight: 800, color: "#0f172a" }}>Vehicles Added</Typography>
                            <Stack direction="row" spacing={0.8} alignItems="baseline" sx={{ mt: 1.5 }}>
                              <Typography sx={{ fontSize: { xs: 24, lg: 28 }, lineHeight: 1, fontWeight: 900, color: "#0f172a" }}>
                                {formatCompactCount(vehiclesAddedCurrentTotal)}
                              </Typography>
                              <Typography sx={{ fontSize: 13, fontWeight: 800, color: "#22c55e" }}>
                                {formatGrowth(vehiclesAddedCurrentTotal, vehiclesAddedPreviousTotal)}
                              </Typography>
                            </Stack>
                            <Typography sx={{ mt: 0.4, fontSize: 11.75, color: "#64748b", fontWeight: 600 }}>
                              vs previous 7 days
                            </Typography>
                          </Box>
                          <Chip label="Last 7 Days" sx={{ bgcolor: "#fff", border: "1px solid #e2e8f0", fontWeight: 700, color: "#64748b", "& .MuiChip-label": { fontSize: 11 } }} />
                        </Stack>
                        <Box sx={{ mt: 1.2, height: 142 }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={addedTrendData}>
                              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e8eef7" />
                              <XAxis dataKey="label" tick={{ fill: "#94a3b8", fontSize: 11 }} axisLine={false} tickLine={false} />
                              <YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                              <RechartsTooltip />
                              <Line type="monotone" dataKey="value" stroke="#2563eb" strokeWidth={2.5} dot={{ r: 3.5, fill: "#2563eb", strokeWidth: 0 }} activeDot={{ r: 4.5 }} />
                            </LineChart>
                          </ResponsiveContainer>
                        </Box>
                      </Paper>

                      <Paper
                        elevation={0}
                        sx={{
                          p: { xs: 1.45, md: 1.7 },
                          borderRadius: 2.5,
                          border: "1px solid #e2e8f0",
                          boxShadow: "0 12px 30px rgba(15, 23, 42, 0.05)",
                          bgcolor: "#fff",
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1.25}>
                          <Box>
                            <Typography sx={{ fontSize: 14.5, fontWeight: 800, color: "#0f172a" }}>Renewals</Typography>
                            <Stack direction="row" spacing={0.8} alignItems="baseline" sx={{ mt: 1.5 }}>
                              <Typography sx={{ fontSize: { xs: 24, lg: 28 }, lineHeight: 1, fontWeight: 900, color: "#0f172a" }}>
                                {formatCompactCount(renewalCurrentTotal)}
                              </Typography>
                              <Typography sx={{ fontSize: 13, fontWeight: 800, color: "#22c55e" }}>
                                {formatGrowth(renewalCurrentTotal, renewalPreviousTotal)}
                              </Typography>
                            </Stack>
                            <Typography sx={{ mt: 0.4, fontSize: 11.75, color: "#64748b", fontWeight: 600 }}>
                              vs previous 6 months
                            </Typography>
                          </Box>
                          <Chip label="Last 6 Months" sx={{ bgcolor: "#fff", border: "1px solid #e2e8f0", fontWeight: 700, color: "#64748b", "& .MuiChip-label": { fontSize: 11 } }} />
                        </Stack>
                        <Box sx={{ mt: 1.2, height: 142 }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={renewalTrendData} barCategoryGap="38%">
                              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e8eef7" />
                              <XAxis dataKey="label" tick={{ fill: "#94a3b8", fontSize: 11 }} axisLine={false} tickLine={false} />
                              <YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                              <RechartsTooltip />
                              <Bar dataKey="value" fill="#22c55e" radius={[7, 7, 0, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        </Box>
                      </Paper>

                      <Paper
                        elevation={0}
                        sx={{
                          p: { xs: 1.45, md: 1.7 },
                          borderRadius: 2.5,
                          border: "1px solid #e2e8f0",
                          boxShadow: "0 12px 30px rgba(15, 23, 42, 0.05)",
                          bgcolor: "#fff",
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1.25}>
                          <Typography sx={{ fontSize: 14.5, fontWeight: 800, color: "#0f172a" }}>Expiring Soon</Typography>
                          <Chip label="Next 30 Days" sx={{ bgcolor: "#fff", border: "1px solid #e2e8f0", fontWeight: 700, color: "#64748b", "& .MuiChip-label": { fontSize: 11 } }} />
                        </Stack>
                        <Box sx={{ mt: 1, display: "grid", gridTemplateColumns: { xs: "1fr", sm: "130px 1fr" }, gap: 1, alignItems: "center" }}>
                          <Box sx={{ height: 138 }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <PieChart>
                                <Pie data={expiringSoonData} dataKey="value" nameKey="name" innerRadius={30} outerRadius={52} paddingAngle={2}>
                                  {expiringSoonData.map((entry) => (
                                    <Cell key={entry.name} fill={entry.color} />
                                  ))}
                                </Pie>
                                <RechartsTooltip />
                              </PieChart>
                            </ResponsiveContainer>
                          </Box>
                          <Box>
                            <Box sx={{ mb: 1.5 }}>
                              <Typography sx={{ fontSize: { xs: 24, lg: 28 }, lineHeight: 1, fontWeight: 900, color: "#0f172a" }}>
                                {formatCompactCount(expiringSoonTotal)}
                              </Typography>
                              <Typography sx={{ mt: 0.35, fontSize: 11.75, color: "#64748b", fontWeight: 600 }}>
                                Vehicles
                              </Typography>
                            </Box>
                            <Stack spacing={0.85}>
                              {expiringSoonData.map((item) => (
                                <Stack key={item.name} direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                                  <Stack direction="row" spacing={0.8} alignItems="center">
                                    <Box sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: item.color }} />
                                    <Typography sx={{ fontSize: 12, color: "#64748b", fontWeight: 700 }}>{item.name}</Typography>
                                  </Stack>
                                  <Typography sx={{ fontSize: 12, color: "#0f172a", fontWeight: 800 }}>
                                    {formatCompactCount(item.value)}
                                  </Typography>
                                </Stack>
                              ))}
                            </Stack>
                          </Box>
                        </Box>
                      </Paper>
                    </Box>
                  </Box>
                </Paper>
              </Box>
            </Box>
          ) : (
            <Box sx={{ p: 1.5 }}>
              <RegisterDeviceModal
                open
                mode={modalMode === "edit" ? "edit" : "create"}
                initialDevice={editingDevice}
                onClose={closeForm}
                onCreated={async () => {
                  await refetch();
                  closeForm();
                }}
                onUpdated={async () => {
                  await refetch();
                  closeForm();
                }}
              />
            </Box>
          )}
        </InnerShell>
      </OuterShell>

      {isMobile ? (
        <Dialog open={mobilePanelOpen && Boolean(selectedPanelRow) && modalMode === "table"} onClose={() => setMobilePanelOpen(false)} fullScreen>
          <DialogContent sx={{ p: 0, bgcolor: "#f8fbff" }}>
            <Box sx={{ px: 2, py: 1.75, bgcolor: "#fff", borderBottom: "1px solid #e2e8f0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Stack direction="row" spacing={1.2} alignItems="center">
                <Avatar sx={{ width: 40, height: 40, bgcolor: "#dbeafe", color: "#2563eb" }}>
                  <OpenInFullRoundedIcon />
                </Avatar>
                <Box>
                  <Typography sx={{ fontSize: 17, fontWeight: 900, color: "#0f172a" }}>Selected Device</Typography>
                  <Typography sx={{ fontSize: 12.5, color: "#64748b", fontWeight: 600 }}>Quick actions and details</Typography>
                </Box>
              </Stack>
              <IconButton onClick={() => setMobilePanelOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
            <Box sx={{ p: 2 }}>{selectedDevicePanel}</Box>
          </DialogContent>
        </Dialog>
      ) : null}

      <Dialog
        open={Boolean(menuDevice)}
        onClose={() => setMenuDevice(null)}
        fullWidth={false}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: "min(720px, calc(100vw - 28px))",
            borderRadius: 3,
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle sx={{ px: 2.25, py: 1.6, borderBottom: "1px solid #e2e8f0" }}>
          <Stack direction="row" spacing={1.2} alignItems="center" justifyContent="space-between">
            <Stack direction="row" spacing={1.2} alignItems="center" sx={{ minWidth: 0 }}>
              <Avatar sx={{ width: 38, height: 38, bgcolor: "#dbeafe", color: "#2563eb" }}>
                <DirectionsCarFilledOutlinedIcon sx={{ fontSize: 20 }} />
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 15, fontWeight: 800, color: "#0f172a" }}>
                  {menuDevice?.vehicle || "Device Actions"}
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: "#64748b", fontWeight: 700 }}>
                  {menuDevice?.imei || "Choose an action for this device"}
                </Typography>
              </Box>
            </Stack>
            <IconButton onClick={() => setMenuDevice(null)} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: 2, bgcolor: "#f8fbff" }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(3, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))" },
              gap: 1,
            }}
          >
            {buildDeviceActionItems(menuDevice).map((action) => {
              const { key, ...tileProps } = action;
              return <DeviceActionTile key={key} {...tileProps} />;
            })}
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(assignUserDevice)}
        onClose={() => { setAssignUserDevice(null); setAssignUserId(""); }}
        fullWidth={false}
        maxWidth={false}
        PaperProps={{ sx: { ...NARROW_DIALOG_PAPER_SX, mt: 6 } }}
      >
        <DialogTitle>Assign Device User</DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            label="Select User"
            value={assignUserId}
            onChange={(event) => setAssignUserId(event.target.value)}
            sx={{ mt: 1 }}
          >
            <MenuItem value="">Unassign</MenuItem>
            {users.map((user) => (
              <MenuItem key={user._id} value={user._id}>
                {user?.username || user?.name || user?.mobileNo || "User"}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setAssignUserDevice(null); setAssignUserId(""); }}>Cancel</Button>
          <Button onClick={handleAssignUser} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(assignGroupDevice)}
        onClose={() => { setAssignGroupDevice(null); setAssignGroupId(""); }}
        fullWidth={false}
        maxWidth={false}
        PaperProps={{ sx: NARROW_DIALOG_PAPER_SX }}
      >
        <DialogTitle>Assign Device Group</DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            label="Select Group"
            value={assignGroupId}
            onChange={(event) => setAssignGroupId(event.target.value)}
            sx={{ mt: 1 }}
          >
            {groups.map((group) => (
              <MenuItem key={group._id} value={group._id}>
                {group?.name || "Group"}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setAssignGroupDevice(null); setAssignGroupId(""); }}>Cancel</Button>
          <Button onClick={handleAssignGroup} variant="contained">Assign</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(extendValidityDevice)}
        onClose={closeExtendValidityDialog}
        fullWidth={false}
        maxWidth={false}
        PaperProps={{ sx: COMPACT_DIALOG_PAPER_SX }}
      >
        <DialogTitle>Extend Validity</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              fullWidth
              label="Type"
              value="yearly"
              disabled
              sx={inputSx}
            >
              <MenuItem value="yearly">Yearly</MenuItem>
            </TextField>

            <TextField
              select
              fullWidth
              label="Renewal for"
              value={extendValidityYears}
              onChange={(event) => setExtendValidityYears(Number(event.target.value))}
              sx={inputSx}
            >
              {EXTEND_VALIDITY_OPTIONS.map((years) => (
                <MenuItem key={years} value={years}>
                  {years} {years === 1 ? "year" : "years"}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              fullWidth
              label="Remarks"
              multiline
              minRows={4}
              value={extendValidityRemark}
              onChange={(event) => setExtendValidityRemark(event.target.value)}
              sx={inputSx}
            />

            <Box>
              <Typography sx={{ fontWeight: 700, color: "#374151", mb: 1 }}>Renew from</Typography>
              <RadioGroup
                row
                value={extendValidityFrom}
                onChange={(event) => setExtendValidityFrom(event.target.value)}
              >
                <FormControlLabel value="original" control={<Radio />} label="Original Date" />
                <FormControlLabel value="current" control={<Radio />} label="Current Date" />
              </RadioGroup>
              <Typography variant="body2" sx={{ color: "#6b7280", mt: 0.5 }}>
                Base date: {formatDate(extendValidityBaseDate)}
              </Typography>
              <Typography variant="body2" sx={{ color: "#6b7280" }}>
                New expiry: {formatDate(extendValidityNextExpiry)}
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeExtendValidityDialog} disabled={updatingDevice}>Cancel</Button>
          <Button onClick={handleExtendValidity} variant="contained" disabled={updatingDevice}>
            {updatingDevice ? "Saving..." : "Extend Validity"}
          </Button>
        </DialogActions>
      </Dialog>

      <DeviceNotificationSettingsDialog
        open={Boolean(notificationDevice)}
        onClose={() => setNotificationDevice(null)}
        device={notificationDevice}
        showStatus={showStatus}
      />

      <Dialog
        open={Boolean(userInfoDialog)}
        onClose={() => setUserInfoDialog(null)}
        fullWidth={false}
        maxWidth={false}
        sx={{
          "& .MuiDialog-container": {
            alignItems: "flex-start",
          },
        }}
        PaperProps={{ sx: NARROW_DIALOG_PAPER_SX }}
      >
        <DialogTitle sx={{ px: 2.25, py: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between", fontWeight: 800 }}>
          {userInfoDialog?.title || "User Details"}
          <IconButton onClick={() => setUserInfoDialog(null)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 2.25, pt: 0.75, pb: 0.5 }}>
          <Stack spacing={1.2}>
            {userInfoDialog?.entries?.length > 1 ? (
              <TextField
                select
                label="Name"
                value={userInfoDialog?.selectedId || ""}
                onChange={(event) =>
                  setUserInfoDialog((prev) => ({ ...prev, selectedId: event.target.value }))
                }
                fullWidth
                sx={inputSx}
              >
                {userInfoDialog.entries.map((entry) => (
                  <MenuItem key={entry.id || entry.label} value={entry.id}>
                    {entry.summary?.name || entry.label}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <TextField
                label="Name"
                value={selectedUserInfoEntry?.summary?.name || selectedUserInfoEntry?.label || "-"}
                fullWidth
                InputProps={{ readOnly: true }}
                sx={inputSx}
              />
            )}

            <Stack direction="row" spacing={0.8}>
              <TextField
                label="Login Id"
                value={selectedUserInfoEntry?.summary?.uniqueId || "-"}
                fullWidth
                InputProps={{ readOnly: true }}
                sx={inputSx}
              />
              <Tooltip title="Copy Login Id">
                <IconButton
                  onClick={() => copyToClipboard("Login Id", selectedUserInfoEntry?.summary?.uniqueId)}
                  sx={{ alignSelf: "stretch", bgcolor: "#fbbf24", color: "#111827", borderRadius: 1.5, width: 48 }}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>

            <Stack direction="row" spacing={0.8}>
              <TextField
                label="Password"
                value={
                  selectedUserInfoEntry?.id && Object.prototype.hasOwnProperty.call(revealedPasswords, selectedUserInfoEntry.id)
                    ? revealedPasswords[selectedUserInfoEntry.id]
                    : "••••••••"
                }
                fullWidth
                InputProps={{ readOnly: true }}
                sx={inputSx}
              />
              <Tooltip
                title={
                  selectedUserInfoEntry?.id && Object.prototype.hasOwnProperty.call(revealedPasswords, selectedUserInfoEntry.id)
                    ? "Hide Password"
                    : "Show Password"
                }
              >
                <IconButton
                  onClick={() => handleTogglePassword(selectedUserInfoEntry?.id)}
                  disabled={!selectedUserInfoEntry?.id || loadingPasswordIds.has(selectedUserInfoEntry?.id)}
                  sx={{ alignSelf: "stretch", bgcolor: "#fbbf24", color: "#111827", borderRadius: 1.5, width: 48 }}
                >
                  {loadingPasswordIds.has(selectedUserInfoEntry?.id) ? (
                    <CircularProgress size={18} sx={{ color: "#111827" }} />
                  ) : selectedUserInfoEntry?.id && Object.prototype.hasOwnProperty.call(revealedPasswords, selectedUserInfoEntry.id) ? (
                    <VisibilityOutlinedIcon fontSize="small" />
                  ) : (
                    <VisibilityOffOutlinedIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
              <Tooltip title="Copy Password">
                <IconButton
                  onClick={() => copyToClipboard("Password", revealedPasswords[selectedUserInfoEntry?.id] || "")}
                  disabled={!selectedUserInfoEntry?.id || !Object.prototype.hasOwnProperty.call(revealedPasswords, selectedUserInfoEntry?.id || "")}
                  sx={{ alignSelf: "stretch", bgcolor: "#fbbf24", color: "#111827", borderRadius: 1.5, width: 48 }}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>

            <Stack direction="row" spacing={0.8}>
              <TextField
                label="Contact"
                value={selectedUserInfoEntry?.summary?.mobileNo || "-"}
                fullWidth
                InputProps={{ readOnly: true }}
                sx={inputSx}
              />
              <Tooltip title="Copy Contact">
                <IconButton
                  onClick={() => copyToClipboard("Contact", selectedUserInfoEntry?.summary?.mobileNo)}
                  sx={{ alignSelf: "stretch", bgcolor: "#fbbf24", color: "#111827", borderRadius: 1.5, width: 48 }}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 2.25, pb: 1.5 }}>
          <Button variant="contained" color="inherit" onClick={() => setUserInfoDialog(null)}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <InstallationCertificateModal
        open={Boolean(certificateDevice)}
        device={certificateDevice}
        onClose={() => setCertificateDevice(null)}
      />

      {sensorDevice ? <SensorAssignModal device={sensorDevice} onClose={() => setSensorDevice(null)} /> : null}

      {reportDevice ? (
        <ReportsPortal
          isOpen
          onClose={() => setReportDevice(null)}
          allDevices={[reportDevice]}
          rawData={[reportDevice]}
        />
      ) : null}
    </>
  );
};

export default DeviceListPortalModal;
